import axios from "axios";
import { action } from "mobx";
import { AuthExperienceConfig } from "../features/auth/config/authExperience";
import { BusinessHomeProfile, ClientAvailabilityProfile, HomeHiringJobProfile, HomeHiringJobProfileSearch, ListingSearchInput, WebsiteInquiry } from "../models/businessHomeProfile";
import { CaregiverJobProfile, CaregiverJobProfileSearch } from "../models/caregiverProfile";
import { BinderDocument, BinderDocumentAnalysis, BinderDocumentVersion, BinderFolder, BinderRule, BinderRuleChangeAlert, BinderRuleSnapshot, BinderRuleSource, BinderShare } from "../models/binder";
import { Document, DocumentFolder, SharedFolder } from "../models/document";
import { NurseDirectoryEntry, ProviderDirectoryCurrentEntriesPage, ProviderDirectoryDashboard, ProviderDirectoryExportResult, ProviderDirectoryMapSearchResult, ProviderDirectoryReportChange, ProviderDirectoryReportDocument, ProviderDirectoryReportRun, ProviderDirectoryRun } from "../models/providerDirectory";
import { ResidentProfile } from "../models/residentProfile";
import { AuthStore } from "./authStore";
import { getSessionBrandOverride } from "../features/auth/config/brandOverride";
import { IntakeSettings, PublicIntakeConfig } from "../models/intake";

enum HTTPMethod {
  GET,
  POST,
  PUT,
  DELETE,
  PATCH,
}

export class BackendAPIStore {
  readonly authStore: AuthStore;
  private jwtToken: string = "";
  private unauthorizedRecoveryPromise: Promise<void> | null = null;
  private pendingFrontendTelemetry: any[] = [];
  private frontendTelemetryTimer: number | null = null;
  private HOME_API_ENDPOINT: string = process.env.REACT_APP_HOME_API_ENDPOINT!;
  private CAREGIVER_API_ENDPOINT: string = process.env.REACT_APP_CAREGIVER_API_ENDPOINT!;
  private FAMILY_API_ENDPOINT: string = process.env.REACT_APP_FAMILY_API_ENDPOINT!;
  private RESIDENT_API_ENDPOINT: string = process.env.REACT_APP_RESIDENT_API_ENDPOINT!;
  private DOCUMENT_API_ENDPOINT: string = process.env.REACT_APP_DOCUMENT_API_ENDPOINT!;
  private BINDER_API_ENDPOINT: string = process.env.REACT_APP_BINDER_API_ENDPOINT!;
  private PROVIDER_DIRECTORY_API_ENDPOINT: string = process.env.REACT_APP_PROVIDER_DIRECTORY_API_ENDPOINT!;
  private HOME_WEBSITE_API_ENDPOINT: string = process.env.REACT_APP_HOME_WEBSITE_API_ENDPOINT!;
  private USER_REVIEW_API_ENDPOINT: string = process.env.REACT_APP_USER_REVIEW_API_ENDPOINT!;
  private AUTH_API_ENDPOINT: string = process.env.REACT_APP_AUTH_API_ENDPOINT!;
  private HOME_LINKS_API_ENDPOINT: string = process.env.REACT_APP_HOME_LINKS_API_ENDPOINT!;
  private NURSE_API_ENDPOINT: string = process.env.REACT_APP_NURSE_API_ENDPOINT!;
  private VENDOR_API_ENDPOINT: string = process.env.REACT_APP_VENDOR_API_ENDPOINT!;
  private RELATIONSHIP_API_ENDPOINT: string = process.env.REACT_APP_RELATIONSHIP_API_ENDPOINT!;
  private DOC_PARSER_API_ENDPOINT: string = process.env.REACT_APP_DOC_PARSER_API_ENDPOINT!;
  private REMINDER_API_ENDPOINT: string = process.env.REACT_APP_REMINDER_API_ENDPOINT!;
  private MEDICATION_API_ENDPOINT: string = process.env.REACT_APP_MEDICATION_API_ENDPOINT!;
  private SHARED_CALENDAR_API_ENDPOINT: string = process.env.REACT_APP_SHARED_CALENDAR_API_ENDPOINT || process.env.REACT_APP_AUTH_API_ENDPOINT!;

  constructor(authStore: AuthStore) {
    this.authStore = authStore;
  }

  @action async init() {
    const isUserLoggedIn = await this.authStore.isUserLoggedIn();
    if (isUserLoggedIn) {
      this.jwtToken = await this.authStore.waitForJwtToken();
    }
  }

  public async getPublicAuthExperience(host: string, brandOverride: string = ""): Promise<AuthExperienceConfig> {
    const params = new URLSearchParams();
    if (host) {
      params.set("host", host);
    }
    if (brandOverride) {
      params.set("brandOverride", brandOverride);
    }

    try {
      const response = await axios.get(
        `${this.AUTH_API_ENDPOINT}/publicAuth${params.toString() ? `?${params.toString()}` : ""}`,
      );
      return response.data;
    } catch (error: any) {
      throw new Error(
        error?.response?.data?.error
        || error?.response?.data
        || error?.message
        || "Unable to load auth experience.",
      );
    }
  }

  public async signUpPublicUser(payload: {
    host: string;
    brandOverride?: string;
    email: string;
    firstName: string;
    lastName: string;
    username: string;
    password: string;
    phoneNumber: string;
    roleType?: string;
    roleTypes?: string[];
    facilityType?: string;
    agreementAcceptance: {
      termsOfService: boolean;
      privacyPolicy: boolean;
      businessAssociateAgreement: boolean;
      acceptedVia: string;
      routePath: string;
      assentTextShown: string;
      agreementVersions: {
        termsOfService: string;
        privacyPolicy: string;
        businessAssociateAgreement: string;
      };
      acceptedDocumentPaths: {
        termsOfService: string;
        privacyPolicy: string;
        businessAssociateAgreement: string;
      };
    };
  }) {
    try {
      const response = await axios.post(
        `${this.AUTH_API_ENDPOINT}/publicAuth`,
        payload,
      );
      return response.data;
    } catch (error: any) {
      throw new Error(
        error?.response?.data?.error
        || error?.response?.data
        || error?.message
        || "Unable to sign up user.",
      );
    }
  }

  public async getPublicDemoSlots() {
    try {
      const response = await axios.get(`${this.AUTH_API_ENDPOINT}/demoSlots`);
      return response.data;
    } catch (error: any) {
      throw new Error(
        error?.response?.data?.error
        || error?.response?.data
        || error?.message
        || "Unable to load demo slots.",
      );
    }
  }

  public async registerPublicDemo(payload: {
    slotId: string;
    fullName: string;
    email: string;
  }) {
    try {
      const response = await axios.post(`${this.AUTH_API_ENDPOINT}/demoRegistrations`, payload, {
        headers: this.jwtToken ? { Authorization: this.jwtToken } : undefined,
      });
      return response.data;
    } catch (error: any) {
      throw new Error(
        error?.response?.data?.error
        || error?.response?.data
        || error?.message
        || "Unable to reserve the demo slot.",
      );
    }
  }

  public async cancelPublicDemoRegistration(cancelToken: string) {
    try {
      const response = await axios.delete(
        `${this.AUTH_API_ENDPOINT}/demoRegistrations?cancelToken=${encodeURIComponent(cancelToken)}`,
      );
      return response.data;
    } catch (error: any) {
      throw new Error(
        error?.response?.data?.error
        || error?.response?.data
        || error?.message
        || "Unable to cancel the demo registration.",
      );
    }
  }

  public async getDemoAdminData() {
    return await this.invokeApi(
      HTTPMethod.GET,
      `${this.AUTH_API_ENDPOINT}/demoAdmin`,
    );
  }

  public async createDemoSlot(payload: any) {
    return await this.invokeApi(
      HTTPMethod.POST,
      `${this.AUTH_API_ENDPOINT}/demoAdmin`,
      payload,
    );
  }

  public async updateDemoSlot(payload: any) {
    return await this.invokeApi(
      HTTPMethod.PUT,
      `${this.AUTH_API_ENDPOINT}/demoAdmin`,
      payload,
    );
  }

  public async generateDemoFeatureNotes(payload: {
    demoTitle?: string;
    selectedFeatures: string[];
  }) {
    return await this.invokeApi(
      HTTPMethod.POST,
      `${this.AUTH_API_ENDPOINT}/demoAdmin`,
      {
        action: "generate_feature_notes",
        demoTitle: payload?.demoTitle || "",
        selectedFeatures: Array.isArray(payload?.selectedFeatures) ? payload.selectedFeatures : [],
      },
    );
  }

  public async cancelDemoSlot(slotId: string) {
    return await this.invokeApi(
      HTTPMethod.DELETE,
      `${this.AUTH_API_ENDPOINT}/demoAdmin?slotId=${encodeURIComponent(slotId)}`,
    );
  }

  public async getCurrentUserContext(host: string, brandOverride: string = "") {
    const params = new URLSearchParams();
    if (host) {
      params.set("host", host);
    }
    if (brandOverride) {
      params.set("brandOverride", brandOverride);
    }

    return await this.invokeApi(
      HTTPMethod.GET,
      `${this.AUTH_API_ENDPOINT}/me/context${params.toString() ? `?${params.toString()}` : ""}`,
    );
  }

  public async getCurrentWorkspaceOnboarding(payload: {
    host?: string;
    brandOverride?: string;
  }) {
    const params = new URLSearchParams();
    if (payload.host) {
      params.set("host", payload.host);
    }
    if (payload.brandOverride) {
      params.set("brandOverride", payload.brandOverride);
    }

    return await this.invokeApi(
      HTTPMethod.GET,
      `${this.AUTH_API_ENDPOINT}/me/onboarding${params.toString() ? `?${params.toString()}` : ""}`,
    );
  }

  public async completeCurrentWorkspaceOnboarding(payload: {
    host?: string;
    brandOverride?: string;
    roleTypes: string[];
    setupMode: string;
    providerOneId?: string;
    profileDraft?: any;
    facilityType?: string;
    facilityName?: string;
    facilityDraft?: any;
    licenseNumber?: string;
    faxNumber?: string;
  }) {
    const params = new URLSearchParams();
    if (payload.host) {
      params.set("host", payload.host);
    }
    if (payload.brandOverride) {
      params.set("brandOverride", payload.brandOverride);
    }

    return await this.invokeApi(
      HTTPMethod.POST,
      `${this.AUTH_API_ENDPOINT}/me/onboarding${params.toString() ? `?${params.toString()}` : ""}`,
      {
        roleTypes: payload.roleTypes,
        setupMode: payload.setupMode,
        providerOneId: payload.providerOneId,
        profileDraft: payload.profileDraft,
        facilityType: payload.facilityType,
        facilityName: payload.facilityName,
        facilityDraft: payload.facilityDraft,
        licenseNumber: payload.licenseNumber,
        faxNumber: payload.faxNumber,
      },
    );
  }

  public async setCurrentUserActiveContext(payload: {
    host?: string;
    brandOverride?: string;
    membershipId?: string;
    roleType?: string;
  }) {
    const params = new URLSearchParams();
    if (payload.host) {
      params.set("host", payload.host);
    }
    if (payload.brandOverride) {
      params.set("brandOverride", payload.brandOverride);
    }

    return await this.invokeApi(
      HTTPMethod.POST,
      `${this.AUTH_API_ENDPOINT}/me/context${params.toString() ? `?${params.toString()}` : ""}`,
      {
        membershipId: payload.membershipId,
        roleType: payload.roleType,
      },
    );
  }

  public async updateCurrentUserWorkspaceRoles(payload: {
    host?: string;
    brandOverride?: string;
    membershipId?: string;
    roleTypes: string[];
  }) {
    const params = new URLSearchParams();
    if (payload.host) {
      params.set("host", payload.host);
    }
    if (payload.brandOverride) {
      params.set("brandOverride", payload.brandOverride);
    }

    return await this.invokeApi(
      HTTPMethod.PUT,
      `${this.AUTH_API_ENDPOINT}/me/context${params.toString() ? `?${params.toString()}` : ""}`,
      {
        membershipId: payload.membershipId,
        roleTypes: payload.roleTypes,
      },
    );
  }

  public async addCurrentUserWorkspaceMembership(payload: {
    host?: string;
    brandOverride?: string;
    facilityType?: string;
    facilityName?: string;
    roleTypes: string[];
  }) {
    const params = new URLSearchParams();
    if (payload.host) {
      params.set("host", payload.host);
    }
    if (payload.brandOverride) {
      params.set("brandOverride", payload.brandOverride);
    }

    return await this.invokeApi(
      HTTPMethod.PATCH,
      `${this.AUTH_API_ENDPOINT}/me/context${params.toString() ? `?${params.toString()}` : ""}`,
      {
        facilityType: payload.facilityType,
        facilityName: payload.facilityName,
        roleTypes: payload.roleTypes,
      },
    );
  }

  public async updateCurrentUserWorkspaceDetails(payload: {
    host?: string;
    brandOverride?: string;
    membershipId: string;
    facilityType?: string;
    facilityName?: string;
    licenseNumber?: string;
    faxNumber?: string;
    facilityDraft?: {
      formattedAddress?: string;
      addressLine1?: string;
      addressLine2?: string;
      city?: string;
      state?: string;
      country?: string;
      zipcode?: string;
      latitude?: string;
      longitude?: string;
      licenseNumber?: string;
      faxNumber?: string;
    };
  }) {
    const params = new URLSearchParams();
    if (payload.host) {
      params.set("host", payload.host);
    }
    if (payload.brandOverride) {
      params.set("brandOverride", payload.brandOverride);
    }

    return await this.invokeApi(
      HTTPMethod.PUT,
      `${this.AUTH_API_ENDPOINT}/me/workspace${params.toString() ? `?${params.toString()}` : ""}`,
      {
        membershipId: payload.membershipId,
        facilityType: payload.facilityType,
        facilityName: payload.facilityName,
        licenseNumber: payload.licenseNumber,
        faxNumber: payload.faxNumber,
        facilityDraft: payload.facilityDraft,
      },
    );
  }

  public async getCurrentWorkspacePermissions(payload: {
    host?: string;
    brandOverride?: string;
  }) {
    const params = new URLSearchParams();
    if (payload.host) {
      params.set("host", payload.host);
    }
    if (payload.brandOverride) {
      params.set("brandOverride", payload.brandOverride);
    }

    return await this.invokeApi(
      HTTPMethod.GET,
      `${this.AUTH_API_ENDPOINT}/me/workspace/permissions${params.toString() ? `?${params.toString()}` : ""}`,
    );
  }

  public async getCurrentRoleDashboard(payload: {
    host?: string;
    brandOverride?: string;
  }) {
    const params = new URLSearchParams();
    if (payload.host) {
      params.set("host", payload.host);
    }
    if (payload.brandOverride) {
      params.set("brandOverride", payload.brandOverride);
    }

    return await this.invokeApi(
      HTTPMethod.GET,
      `${this.AUTH_API_ENDPOINT}/me/dashboard${params.toString() ? `?${params.toString()}` : ""}`,
    );
  }

  public async listCurrentWorkspaceMembers(payload: {
    host?: string;
    brandOverride?: string;
    membershipId?: string;
  }) {
    const params = new URLSearchParams();
    if (payload.host) {
      params.set("host", payload.host);
    }
    if (payload.brandOverride) {
      params.set("brandOverride", payload.brandOverride);
    }
    if (payload.membershipId) {
      params.set("membershipId", payload.membershipId);
    }

    return await this.invokeApi(
      HTTPMethod.GET,
      `${this.AUTH_API_ENDPOINT}/me/workspace/members${params.toString() ? `?${params.toString()}` : ""}`,
    );
  }

  public async updateCurrentWorkspaceMemberRoles(payload: {
    host?: string;
    brandOverride?: string;
    userId: string;
    membershipId: string;
    roleTypes: string[];
  }) {
    const params = new URLSearchParams();
    if (payload.host) {
      params.set("host", payload.host);
    }
    if (payload.brandOverride) {
      params.set("brandOverride", payload.brandOverride);
    }

    return await this.invokeApi(
      HTTPMethod.PUT,
      `${this.AUTH_API_ENDPOINT}/me/workspace/members${params.toString() ? `?${params.toString()}` : ""}`,
      {
        userId: payload.userId,
        membershipId: payload.membershipId,
        roleTypes: payload.roleTypes,
      },
    );
  }

  public async removeCurrentWorkspaceMember(payload: {
    host?: string;
    brandOverride?: string;
    userId: string;
    membershipId: string;
  }) {
    const params = new URLSearchParams();
    if (payload.host) {
      params.set("host", payload.host);
    }
    if (payload.brandOverride) {
      params.set("brandOverride", payload.brandOverride);
    }

    return await this.invokeApi(
      HTTPMethod.DELETE,
      `${this.AUTH_API_ENDPOINT}/me/workspace/members${params.toString() ? `?${params.toString()}` : ""}`,
      {
        userId: payload.userId,
        membershipId: payload.membershipId,
      },
    );
  }

  public async listCurrentWorkspaceInvitations(payload: {
    host?: string;
    brandOverride?: string;
    membershipId?: string;
  }) {
    const params = new URLSearchParams();
    if (payload.host) {
      params.set("host", payload.host);
    }
    if (payload.brandOverride) {
      params.set("brandOverride", payload.brandOverride);
    }
    if (payload.membershipId) {
      params.set("membershipId", payload.membershipId);
    }

    return await this.invokeApi(
      HTTPMethod.GET,
      `${this.AUTH_API_ENDPOINT}/me/workspace/invitations${params.toString() ? `?${params.toString()}` : ""}`,
    );
  }

  public async createCurrentWorkspaceInvitation(payload: {
    host?: string;
    brandOverride?: string;
    targetEmail: string;
    targetName?: string;
    message?: string;
    roleTypes: string[];
  }) {
    const params = new URLSearchParams();
    if (payload.host) {
      params.set("host", payload.host);
    }
    if (payload.brandOverride) {
      params.set("brandOverride", payload.brandOverride);
    }

    return await this.invokeApi(
      HTTPMethod.POST,
      `${this.AUTH_API_ENDPOINT}/me/workspace/invitations${params.toString() ? `?${params.toString()}` : ""}`,
      payload,
    );
  }

  public async resendCurrentWorkspaceInvitation(payload: {
    host?: string;
    brandOverride?: string;
    inviteId: string;
  }) {
    const params = new URLSearchParams();
    if (payload.host) {
      params.set("host", payload.host);
    }
    if (payload.brandOverride) {
      params.set("brandOverride", payload.brandOverride);
    }

    return await this.invokeApi(
      HTTPMethod.PUT,
      `${this.AUTH_API_ENDPOINT}/me/workspace/invitations${params.toString() ? `?${params.toString()}` : ""}`,
      { inviteId: payload.inviteId },
    );
  }

  public async revokeCurrentWorkspaceInvitation(payload: {
    host?: string;
    brandOverride?: string;
    inviteId: string;
  }) {
    const params = new URLSearchParams();
    if (payload.host) {
      params.set("host", payload.host);
    }
    if (payload.brandOverride) {
      params.set("brandOverride", payload.brandOverride);
    }

    return await this.invokeApi(
      HTTPMethod.DELETE,
      `${this.AUTH_API_ENDPOINT}/me/workspace/invitations${params.toString() ? `?${params.toString()}` : ""}`,
      { inviteId: payload.inviteId },
    );
  }

  private clearFrontendTelemetryTimer() {
    if (this.frontendTelemetryTimer) {
      window.clearTimeout(this.frontendTelemetryTimer);
      this.frontendTelemetryTimer = null;
    }
  }

  public queueFrontendTelemetry(events: any[]) {
    if (!this.jwtToken || !events || events.length === 0) {
      return;
    }

    this.pendingFrontendTelemetry.push(...events);

    if (this.pendingFrontendTelemetry.length >= 20) {
      void this.flushFrontendTelemetry();
      return;
    }

    if (!this.frontendTelemetryTimer) {
      this.frontendTelemetryTimer = window.setTimeout(() => {
        void this.flushFrontendTelemetry();
      }, 1200);
    }
  }

  public async flushFrontendTelemetry() {
    if (!this.jwtToken || this.pendingFrontendTelemetry.length === 0) {
      this.clearFrontendTelemetryTimer();
      return;
    }

    this.clearFrontendTelemetryTimer();
    const eventsToSend = [...this.pendingFrontendTelemetry];
    this.pendingFrontendTelemetry = [];
    await this.sendFrontendTelemetry(eventsToSend);
  }

  public async sendFrontendTelemetry(events: any[]) {
    if (!this.jwtToken || !events || events.length === 0) {
      return;
    }

    try {
      await axios.post(
        `${this.AUTH_API_ENDPOINT}/user?frontendTelemetry=true`,
        { events },
        {
          headers: {
            authorization: this.jwtToken,
          },
        },
      );
    } catch (error) {
      console.error("Failed to send frontend telemetry");
    }
  }

  private getCurrentBrandOverride() {
    const appHost = typeof window !== "undefined" ? window.location.host : "";
    return getSessionBrandOverride() || appHost;
  }

  private addBrandContextToUrl(url: string) {
    try {
      const resolvedUrl = new URL(url);
      if (!resolvedUrl.searchParams.has("host") && typeof window !== "undefined") {
        resolvedUrl.searchParams.set("host", window.location.host);
      }
      const brandOverride = getSessionBrandOverride();
      if (brandOverride && !resolvedUrl.searchParams.has("brandOverride")) {
        resolvedUrl.searchParams.set("brandOverride", brandOverride);
      }
      return resolvedUrl.toString();
    } catch {
      return url;
    }
  }

  // API to invoke AmazonQ 

  async invokeApi(httpMethod: HTTPMethod, url: string, requestBody?: object, hasRetriedAfterRefresh: boolean = false) {
    const requestUrl = this.addBrandContextToUrl(url);
    const startedAt = performance.now();
    const telemetryEnabled = !!this.jwtToken && !url.includes("frontendTelemetry=true");
    const endpointPath = (() => {
      try {
        return new URL(requestUrl).pathname;
      } catch {
        return requestUrl;
      }
    })();
    try {
      var response: any;
      const headers = {
        headers: {
          authorization: this.jwtToken,
        },
      };

      if (httpMethod === HTTPMethod.GET) {
        response = await axios.get(requestUrl, headers);
      } else if (httpMethod === HTTPMethod.PUT) {
        response = await axios.put(requestUrl, requestBody, headers);
      } else if (httpMethod === HTTPMethod.POST) {
        response = await axios.post(requestUrl, requestBody, headers);
      } else if (httpMethod === HTTPMethod.DELETE) {
        response = await axios.delete(requestUrl, { ...headers, data: requestBody });
      } else if (httpMethod === HTTPMethod.PATCH) {
        response = await axios.patch(requestUrl, {}, headers);
      }
      if (telemetryEnabled) {
        this.queueFrontendTelemetry([{
          metricType: "api_latency",
          endpoint: endpointPath,
          method: HTTPMethod[httpMethod],
          durationMs: Math.round(performance.now() - startedAt),
          page: window.location.pathname,
        }]);
      }
      return response.data;
    } catch (err) {
      console.error("API request failed");

      if (telemetryEnabled) {
        const statusCode = axios.isAxiosError(err) ? err.response?.status || err.status || 0 : 0;
        this.queueFrontendTelemetry([
          {
            metricType: "api_latency",
            endpoint: endpointPath,
            method: HTTPMethod[httpMethod],
            durationMs: Math.round(performance.now() - startedAt),
            page: window.location.pathname,
          },
          {
            metricType: "api_failure",
            endpoint: endpointPath,
            method: HTTPMethod[httpMethod],
            statusCode,
            page: window.location.pathname,
          },
        ]);
      }

      if (axios.isAxiosError(err)) {
        const statusCode = err.response?.status || err.status || 0;
        if (statusCode === 401 && !hasRetriedAfterRefresh) {
          const recovered = await this.tryRecoverExpiredSession();
          if (recovered) {
            return await this.invokeApi(httpMethod, url, requestBody, true);
          }
        }
        if (statusCode === 400 || statusCode === 401) {
          if (err.response?.data.code) {
            const codedError: any = new Error(err.response?.data.error, { cause: new Error(err.response?.data.code) });
            codedError.code = err.response?.data.code;
            codedError.details = err.response?.data;
            throw codedError;
          }
          if (err.response) {
            if (err.response?.data.error) {
              const apiError: any = new Error(err.response?.data.error);
              apiError.details = err.response?.data;
              throw apiError;
            } else if (err.response?.data) {
              const apiError: any = new Error(err.response?.data);
              apiError.details = err.response?.data;
              throw apiError;
            }
          }
        } else if (statusCode === 409) {
          const apiError: any = new Error(
            err.response?.data?.error
            || (typeof err.response?.data === "string" ? err.response.data : err.message),
          );
          apiError.details = err.response?.data;
          apiError.statusCode = 409;
          throw apiError;
        } else if (err.response) {
          if (err.response?.data?.error) {
            const apiError: any = new Error(err.response.data.error);
            apiError.details = err.response.data;
            apiError.statusCode = statusCode;
            throw apiError;
          } else if (err.response?.data) {
            const apiError: any = new Error(
              typeof err.response.data === "string" ? err.response.data : err.message,
            );
            apiError.details = err.response.data;
            apiError.statusCode = statusCode;
            throw apiError;
          }
        } else if (err instanceof Error) {
          console.error("Request failed");
          throw new Error("Request failed: " + err.message, { cause: err });
        }
      }
    }
  }

  private async tryRecoverExpiredSession(): Promise<boolean> {
    try {
      if (!this.unauthorizedRecoveryPromise) {
        this.unauthorizedRecoveryPromise = (async () => {
          const refreshedToken = await this.authStore.refreshJwtToken();
          this.jwtToken = refreshedToken;
        })().finally(() => {
          this.unauthorizedRecoveryPromise = null;
        });
      }

      await this.unauthorizedRecoveryPromise;
      return !!this.jwtToken;
    } catch (error) {
      console.error("Unable to refresh expired session");
      this.unauthorizedRecoveryPromise = null;

      const recoveryFlag = "afh-auth-reload-attempted";
      const hasReloaded = sessionStorage.getItem(recoveryFlag) === "true";
      if (!hasReloaded) {
        sessionStorage.setItem(recoveryFlag, "true");
        window.location.reload();
        return false;
      }

      sessionStorage.removeItem(recoveryFlag);
      this.authStore.signOut();
      return false;
    }
  }

  public async getJobDetails(jobId: string) {
    return await this.invokeApi(
      HTTPMethod.GET,
      `${this.DOC_PARSER_API_ENDPOINT}/doc-parser-workflow?jobId=${jobId}`,
    );
  }

  public async getLastJobs(userId: string) {
    return await this.invokeApi(
      HTTPMethod.GET,
      `${this.DOC_PARSER_API_ENDPOINT}/doc-parser-workflow?userId=${userId}&lastJobs=true`,
    );
  }

  public async addJobDetails(requestBody: any) {
    return await this.invokeApi(
      HTTPMethod.POST,
      `${this.DOC_PARSER_API_ENDPOINT}/doc-parser-workflow`,
      requestBody,
    );
  }

  public async putJobDetails(
    jobId: string,
    retryOrOptions: boolean | { retry?: boolean; markFailed?: boolean; errorMessage?: string } = false
  ) {
    const options = typeof retryOrOptions === "boolean"
      ? { retry: retryOrOptions }
      : (retryOrOptions || {});
    const requestBody = {
      jobId: jobId,
      retry: Boolean(options.retry),
      markFailed: Boolean(options.markFailed),
      ...(options.errorMessage ? { errorMessage: options.errorMessage } : {}),
    };
    return await this.invokeApi(
      HTTPMethod.PUT,
      `${this.DOC_PARSER_API_ENDPOINT}/doc-parser-workflow`,
      requestBody,
    );
  }

  public async searchAICompanion(queryText: string, searchSource: string) {
    const requestBody = {
      queryText: queryText,
      searchSource: searchSource
    };
    return await this.invokeApi(
      HTTPMethod.POST,
      `${this.HOME_API_ENDPOINT}/aiCompanion`,
      requestBody,
    );
  }

  public async addHomeCaregiverLink(homeId: string, caregiverId: string, options?: {
    primaryRoleType?: string;
    roleTypes?: string[];
    mobileAccessEnabled?: boolean;
    hourlyRate?: number;
    shiftEnabled?: boolean;
    shiftDays?: string[];
    shiftStartTime?: string;
    shiftEndTime?: string;
    shiftWindows?: Array<{ day: string; startTime: string; endTime: string }>;
    shiftTimezone?: string;
  }) {
    const requestBody = {
      homeId: homeId,
      caregiverId: caregiverId,
      primaryRoleType: options?.primaryRoleType,
      roleTypes: options?.roleTypes,
      mobileAccessEnabled: options?.mobileAccessEnabled,
      hourlyRate: options?.hourlyRate,
      shiftEnabled: options?.shiftEnabled,
      shiftDays: options?.shiftDays,
      shiftStartTime: options?.shiftStartTime,
      shiftEndTime: options?.shiftEndTime,
      shiftWindows: options?.shiftWindows,
      shiftTimezone: options?.shiftTimezone,
    };
    return await this.invokeApi(
      HTTPMethod.POST,
      `${this.HOME_API_ENDPOINT}/homeCaregiverLinks`,
      requestBody,
    );
  }

  public async deleteHomeCaregiverLink(linkId: string, homeId: string) {
    return await this.invokeApi(
      HTTPMethod.DELETE,
      `${this.HOME_API_ENDPOINT}/homeCaregiverLinks?linkId=${linkId}&homeId=${homeId}`,
    );
  }

  public async updateHomeCaregiverLinkShift(requestBody: {
    homeId: string;
    caregiverId: string;
    linkId: string;
    shiftEnabled: boolean;
    shiftDays?: string[];
    shiftStartTime?: string;
    shiftEndTime?: string;
    shiftWindows?: Array<{ day: string; startTime: string; endTime: string }>;
    shiftTimezone?: string;
    mobileAccessEnabled?: boolean;
  }) {
    return await this.invokeApi(
      HTTPMethod.PUT,
      `${this.HOME_API_ENDPOINT}/homeCaregiverLinks`,
      requestBody,
    );
  }

  public async updateHomeCaregiverLinkAdminAccess(requestBody: {
    homeId: string;
    caregiverId: string;
    linkId: string;
    facilityAdminAccess: boolean;
  }) {
    return await this.invokeApi(
      HTTPMethod.PUT,
      `${this.HOME_API_ENDPOINT}/homeCaregiverLinks`,
      requestBody,
    );
  }

  public async updateHomeCaregiverLinkStatus(requestBody: {
    homeId: string;
    caregiverId: string;
    linkId: string;
    active: boolean;
  }) {
    return await this.invokeApi(
      HTTPMethod.PUT,
      `${this.HOME_API_ENDPOINT}/homeCaregiverLinks`,
      requestBody,
    );
  }

  public async bulkUpdateHomeCaregiverLinkShift(requestBody: {
    homeId: string;
    caregiverIds: string[];
    shiftEnabled: boolean;
    shiftDays?: string[];
    shiftStartTime?: string;
    shiftEndTime?: string;
    shiftWindows?: Array<{ day: string; startTime: string; endTime: string }>;
    shiftTimezone?: string;
    mobileAccessEnabled?: boolean;
  }) {
    return await this.invokeApi(
      HTTPMethod.PUT,
      `${this.HOME_API_ENDPOINT}/homeCaregiverLinks`,
      {
        ...requestBody,
        bulkUpdate: true,
      },
    );
  }

  public async getLinkedCaregiversForHome(homeId: string) {
    return await this.invokeApi(
      HTTPMethod.GET,
      `${this.HOME_API_ENDPOINT}/homeCaregiverLinks?homeId=${homeId}`,
    );
  }

  public async getLinkedHomesForCaregiver(caregiverId: string) {
    return await this.invokeApi(
      HTTPMethod.GET,
      `${this.HOME_API_ENDPOINT}/homeCaregiverLinks?caregiverId=${caregiverId}`,
    );
  }

  public async listAllReview() {
    return this.invokeApi(
      HTTPMethod.GET,
      `${this.USER_REVIEW_API_ENDPOINT}/userReview?`,
    );
  }

  public async addNewReview(reviewData: any) {
    return await this.invokeApi(
      HTTPMethod.POST,
      `${this.USER_REVIEW_API_ENDPOINT}/userReview`,
      reviewData,
    );
  }

  public async deleteReview(reviewId: string) {
    return await this.invokeApi(
      HTTPMethod.DELETE,
      `${this.USER_REVIEW_API_ENDPOINT}/userReview?reviewId=${reviewId}`,
    );
  }

  public async addCustomerFeedback(feedbackData: any) {
    return await this.invokeApi(
      HTTPMethod.POST,
      `${this.USER_REVIEW_API_ENDPOINT}/customerFeedback`,
      feedbackData,
    );
  }

  public async updateCustomerFeedback(feedbackData: any) {
    return await this.invokeApi(
      HTTPMethod.PUT,
      `${this.USER_REVIEW_API_ENDPOINT}/customerFeedback`,
      feedbackData,
    );
  }

  public async listCustomerFeedback() {
    return this.invokeApi(
      HTTPMethod.GET,
      `${this.USER_REVIEW_API_ENDPOINT}/customerFeedback?`,
    );
  }

  public async listCustomerFeedbackMessages(feedbackId: string) {
    return this.invokeApi(
      HTTPMethod.GET,
      `${this.USER_REVIEW_API_ENDPOINT}/customerFeedback?feedbackId=${encodeURIComponent(feedbackId)}&includeMessages=true`,
    );
  }

  public async replyToCustomerFeedback(feedbackId: string, message: string) {
    return this.invokeApi(
      HTTPMethod.PUT,
      `${this.USER_REVIEW_API_ENDPOINT}/customerFeedback`,
      { action: "reply", feedbackId, message },
    );
  }

  public async getBusinessHomeDetails(id: string) {
    return await this.invokeApi(
      HTTPMethod.GET,
      `${this.HOME_API_ENDPOINT}/home?homeId=${id}`,
    );
  }

  public async getHomeImagesPreSignedUrls(id: string) {
    return await this.invokeApi(
      HTTPMethod.GET,
      `${this.HOME_API_ENDPOINT}/home?homeId=${id}&generatePresignedUrls=true`,
    );
  }

  public async getHomeListForProvider() {
    return await this.invokeApi(
      HTTPMethod.GET,
      `${this.HOME_API_ENDPOINT}/home?list=all`,
    );
  }

  public async getHomeListForUser(username: string) {
    return await this.invokeApi(
      HTTPMethod.GET,
      `${this.HOME_API_ENDPOINT}/home?list=all&username=${encodeURIComponent(username)}`,
    );
  }

  public async addHome(businessHome: BusinessHomeProfile) {
    return await this.invokeApi(
      HTTPMethod.POST,
      `${this.HOME_API_ENDPOINT}/home`,
      businessHome,
    );
  }

  public async deleteHome(homeId: string) {
    return await this.invokeApi(
      HTTPMethod.DELETE,
      `${this.HOME_API_ENDPOINT}/home?homeId=${homeId}`,
    );
  }

  public async updateHome(businessHome: BusinessHomeProfile) {
    return await this.invokeApi(
      HTTPMethod.PUT,
      `${this.HOME_API_ENDPOINT}/home`,
      businessHome,
    );
  }

  public async getIntakeSettings(homeId: string): Promise<IntakeSettings> {
    return await this.invokeApi(
      HTTPMethod.GET,
      `${this.HOME_API_ENDPOINT}/intakeSettings?homeId=${encodeURIComponent(homeId)}`,
    );
  }

  public async updateIntakeSettings(settings: IntakeSettings): Promise<IntakeSettings> {
    return await this.invokeApi(
      HTTPMethod.PUT,
      `${this.HOME_API_ENDPOINT}/intakeSettings`,
      settings,
    );
  }

  public async getPublicIntakeConfig(token: string): Promise<PublicIntakeConfig> {
    const response = await axios.get(
      `${this.HOME_API_ENDPOINT}/publicIntake?token=${encodeURIComponent(token)}`,
    );
    return response.data;
  }

  public async preparePublicIntakeUploads(payload: any) {
    const response = await axios.post(`${this.HOME_API_ENDPOINT}/publicIntakeUpload`, payload);
    return response.data;
  }

  public async uploadPublicIntakeDocument(uploadUrl: string, file: File) {
    await axios.put(uploadUrl, file, { headers: { "Content-Type": file.type } });
  }

  public async submitPublicIntake(payload: any) {
    const response = await axios.post(`${this.HOME_API_ENDPOINT}/publicIntake`, payload);
    return response.data;
  }

  public async getIntakeSubmissions(homeId: string) {
    return await this.invokeApi(
      HTTPMethod.GET,
      `${this.HOME_API_ENDPOINT}/intakeSubmissions?homeId=${encodeURIComponent(homeId)}`,
    );
  }

  public async getIntakeDocumentPreview(homeId: string, intakeSubmissionId: string, documentId: string) {
    return await this.invokeApi(
      HTTPMethod.POST,
      `${this.HOME_API_ENDPOINT}/intakeDocumentPreview`,
      { homeId, intakeSubmissionId, documentId },
    );
  }

  public async getTemplatesDetails(type: string, routeId: string) {
    return await this.invokeApi(
      HTTPMethod.GET,
      `${this.HOME_WEBSITE_API_ENDPOINT}/template?type=${type}&homeId=${routeId}`,
    );
  }

  public async getPublishedWebsiteDetails(state: string, businessName: string) {
    return await this.invokeApi(
      HTTPMethod.GET,
      `${this.HOME_WEBSITE_API_ENDPOINT}/publicWebsite?state=${state}&businessName=${businessName}`,
    );
  }

  public async getPublicPricingCatalog() {
    return await axios.get(
      `${this.HOME_WEBSITE_API_ENDPOINT}/publicWebsite?pricingCatalog=true`,
    ).then((response) => response.data);
  }

  public async getPublicLiveDemoSample(sampleId: string = "assessment") {
    return await axios.get(
      `${this.HOME_WEBSITE_API_ENDPOINT}/liveDemoSample?sampleId=${encodeURIComponent(sampleId)}`,
    ).then((response) => response.data);
  }

  public async getPublicReleaseNotes(filters?: {
    q?: string;
    dateFrom?: string;
    dateTo?: string;
  }) {
    const params = new URLSearchParams();
    params.set("releaseNotes", "true");
    if (String(filters?.q || "").trim()) {
      params.set("q", String(filters?.q || "").trim());
    }
    if (String(filters?.dateFrom || "").trim()) {
      params.set("dateFrom", String(filters?.dateFrom || "").trim());
    }
    if (String(filters?.dateTo || "").trim()) {
      params.set("dateTo", String(filters?.dateTo || "").trim());
    }

    return await axios.get(
      `${this.HOME_WEBSITE_API_ENDPOINT}/publicWebsite?${params.toString()}`,
    ).then((response) => response.data);
  }

  public async updateTemplateSection(data: any) {
    return await this.invokeApi(
      HTTPMethod.PUT,
      `${this.HOME_WEBSITE_API_ENDPOINT}/template`,
      data,
    );
  }

  public async updateWebsiteUrl(data: any) {
    return await this.invokeApi(
      HTTPMethod.PUT,
      `${this.HOME_WEBSITE_API_ENDPOINT}/template?updateWebsiteUrl=true`,
      data,
    );
  }

  public async updateWebsiteTemplateSelection(data: any) {
    return await this.invokeApi(
      HTTPMethod.PUT,
      `${this.HOME_WEBSITE_API_ENDPOINT}/template?updateTemplateSelection=true`,
      data,
    );
  }

  public async resetWebsiteDraft(data: any) {
    return await this.invokeApi(
      HTTPMethod.PUT,
      `${this.HOME_WEBSITE_API_ENDPOINT}/template?resetDraft=true`,
      data,
    );
  }

  public async publishTemplate(data: any) {
    return await this.invokeApi(
      HTTPMethod.POST,
      `${this.HOME_WEBSITE_API_ENDPOINT}/template`,
      data,
    );
  }

  public async enableDisableTemplate(homeId: string, status: boolean) {
    return await this.invokeApi(
      HTTPMethod.PATCH,
      `${this.HOME_WEBSITE_API_ENDPOINT}/template?homeId=${homeId}&isWebsiteActive=${status}`,
    );
  }

  public async getLinkedUsersForHome(homeId: string) {
    return await this.invokeApi(
      HTTPMethod.GET,
      `${this.HOME_LINKS_API_ENDPOINT}/homeLinks?homeId=${homeId}`,
    );
  }

  public async getLinkedHomesForUser() {
    return await this.invokeApi(
      HTTPMethod.GET,
      `${this.HOME_LINKS_API_ENDPOINT}/homeLinks`,
    );
  }

  public async searchUsers(emailId: string, homeId: string) {
    const requestBody = {
      emailId: emailId,
      homeId: homeId,
    };
    return await this.invokeApi(
      HTTPMethod.POST,
      `${this.AUTH_API_ENDPOINT}/user?search=true`,
      requestBody
    );
  }

  public async addHomeLink(requestBody: any) {
    return await this.invokeApi(
      HTTPMethod.POST,
      `${this.HOME_LINKS_API_ENDPOINT}/homeLinks`,
      requestBody
    );
  }

  public async deleteHomeLink(userId: string, homeId: string) {
    return await this.invokeApi(
      HTTPMethod.DELETE,
      `${this.HOME_LINKS_API_ENDPOINT}/homeLinks?userId=${userId}&homeId=${homeId}`,
    );
  }

  public async getNurseProviders() {
    return await this.invokeApi(
      HTTPMethod.GET,
      `${this.NURSE_API_ENDPOINT}/nurseProviders`,
    );
  }

  public async getNurseDirectoryVerificationStatus() {
    return await this.invokeApi(
      HTTPMethod.GET,
      `${this.NURSE_API_ENDPOINT}/nurseDirectoryVerification`,
    );
  }

  public async addNurseProvider(requestBody: {
    providerName: string;
    afhAddress: string;
    phone: string;
    email: string;
    linkedUserId?: string;
  }) {
    return await this.invokeApi(
      HTTPMethod.POST,
      `${this.NURSE_API_ENDPOINT}/nurseProviders`,
      requestBody,
    );
  }

  public async updateNurseProvider(requestBody: {
    providerId: string;
    providerName: string;
    afhAddress: string;
    phone: string;
    email: string;
    linkedUserId?: string;
    active?: boolean;
  }) {
    return await this.invokeApi(
      HTTPMethod.PUT,
      `${this.NURSE_API_ENDPOINT}/nurseProviders`,
      requestBody,
    );
  }

  public async lookupNurseProviderByEmail(email: string) {
    return await this.invokeApi(
      HTTPMethod.GET,
      `${this.NURSE_API_ENDPOINT}/nurseProviders?lookupByEmail=true&email=${encodeURIComponent(email)}`,
    );
  }

  public async deleteNurseProvider(providerId: string) {
    return await this.invokeApi(
      HTTPMethod.DELETE,
      `${this.NURSE_API_ENDPOINT}/nurseProviders?providerId=${providerId}`,
    );
  }

  public async permanentlyDeleteNurseProvider(providerId: string) {
    return await this.invokeApi(
      HTTPMethod.DELETE,
      `${this.NURSE_API_ENDPOINT}/nurseProviders?providerId=${providerId}&action=delete`,
    );
  }

  public async getNurseProviderResidents(providerId: string) {
    return await this.invokeApi(
      HTTPMethod.GET,
      `${this.NURSE_API_ENDPOINT}/nurseProviderResidents?providerId=${providerId}`,
    );
  }

  public async getNurseResidentProfile(residentId: string, includeInactive = false) {
    const params = new URLSearchParams({ residentId });
    if (includeInactive) params.set("includeInactive", "true");
    return await this.invokeApi(
      HTTPMethod.GET,
      `${this.NURSE_API_ENDPOINT}/nurseProviderResidents?${params.toString()}`,
    );
  }

  public async addNurseProviderResident(requestBody: {
    providerId: string;
    firstName: string;
    lastName: string;
  }) {
    return await this.invokeApi(
      HTTPMethod.POST,
      `${this.NURSE_API_ENDPOINT}/nurseProviderResidents`,
      requestBody,
    );
  }

  public async updateNurseProviderResident(requestBody: {
    residentId: string;
    firstName: string;
    lastName: string;
    active?: boolean;
    isOnMedicaid?: boolean;
    medicaidClientId?: string;
    providerOneNumber?: string;
    primaryInsuranceName?: string;
    primaryInsurancePlanType?: string;
    primaryInsuranceMemberId?: string;
    primaryInsuranceGroupNumber?: string;
    primaryInsuranceSubscriberName?: string;
    primaryInsuranceSubscriberRelationship?: string;
    primaryInsurancePhone?: string;
    primaryInsuranceClaimsPhone?: string;
    secondaryInsuranceName?: string;
    secondaryInsurancePlanType?: string;
    secondaryInsuranceMemberId?: string;
    secondaryInsuranceGroupNumber?: string;
    secondaryInsuranceSubscriberName?: string;
    secondaryInsuranceSubscriberRelationship?: string;
    secondaryInsurancePhone?: string;
    secondaryInsuranceClaimsPhone?: string;
    insuranceNotes?: string;
  }) {
    return await this.invokeApi(
      HTTPMethod.PUT,
      `${this.NURSE_API_ENDPOINT}/nurseProviderResidents`,
      requestBody,
    );
  }

  public async deleteNurseProviderResident(residentId: string) {
    return await this.invokeApi(
      HTTPMethod.DELETE,
      `${this.NURSE_API_ENDPOINT}/nurseProviderResidents?residentId=${residentId}`,
    );
  }

  public async getNurseProviderStaff(providerId: string) {
    return await this.invokeApi(HTTPMethod.GET, `${this.NURSE_API_ENDPOINT}/nurseProviderStaff?providerId=${encodeURIComponent(providerId)}`);
  }

  public async saveNurseProviderStaff(requestBody: {
    providerId: string;
    staffId?: string;
    name: string;
    email: string;
    dateOfBirth?: string;
    credentialType?: string;
    credentialNumber?: string;
    credentialIssueDate?: string;
    credentialExpiryDate?: string;
    coreTrainingCompleted?: boolean;
    coreTrainingDate?: string;
    diabetesTrainingCompleted?: boolean;
    diabetesTrainingDate?: string;
    narBasicTrainingCompleted?: boolean;
    narBasicTrainingDate?: string;
    basicTrainingCoreCompetencyCompleted?: boolean;
    basicTrainingCoreCompetencyDate?: string;
    basicTraining75HoursCompleted?: boolean;
    basicTraining75HoursDate?: string;
    fundamentalsOfCareCompleted?: boolean;
    fundamentalsOfCareDate?: string;
    revisedFundamentalsOfCareCompleted?: boolean;
    revisedFundamentalsOfCareDate?: string;
    dda32HourLetterCompleted?: boolean;
    dda32HourLetterDate?: string;
    exemptLtcwCompleted?: boolean;
    exemptLtcwDate?: string;
  }) {
    return await this.invokeApi(HTTPMethod.POST, `${this.NURSE_API_ENDPOINT}/nurseProviderStaff`, requestBody);
  }

  public async saveStaffAccount(requestBody: {
    roleType: string;
    staffId?: string;
    name: string;
    email: string;
    headerInvite?: boolean;
    staffDetails: Record<string, any>;
  }) {
    return await this.invokeApi(HTTPMethod.POST, `${this.AUTH_API_ENDPOINT}/staffAccount`, requestBody);
  }

  public async getStaffAccount(userId?: string, email?: string) {
    const query = userId
      ? `userId=${encodeURIComponent(userId)}`
      : `email=${encodeURIComponent(email || "")}`;
    return await this.invokeApi(HTTPMethod.GET, `${this.AUTH_API_ENDPOINT}/staffAccount?${query}`);
  }

  public async deleteNurseProviderStaff(providerId: string, staffId: string) {
    return await this.invokeApi(HTTPMethod.DELETE, `${this.NURSE_API_ENDPOINT}/nurseProviderStaff?providerId=${encodeURIComponent(providerId)}&staffId=${encodeURIComponent(staffId)}`);
  }

  public async getNurseResidentAssessment(residentId: string, formId?: string) {
    const formQuery = formId ? `&formId=${encodeURIComponent(formId)}` : "";
    return await this.invokeApi(
      HTTPMethod.GET,
      `${this.NURSE_API_ENDPOINT}/nurseResidentAssessments?residentId=${residentId}${formQuery}`,
    );
  }

  public async getProviderResidentAssessment(residentId: string, formId?: string) {
    const formQuery = formId ? `&formId=${encodeURIComponent(formId)}` : "";
    return await this.invokeApi(
      HTTPMethod.GET,
      `${this.RESIDENT_API_ENDPOINT}/residentAssessment?residentId=${residentId}${formQuery}`,
    );
  }

  public async addNurseResidentAssessmentDocument(requestBody: {
    residentId: string;
    documentId: string;
    fileName: string;
    tag?: string;
    tags?: string[];
    objectKey: string;
  }) {
    return await this.invokeApi(
      HTTPMethod.POST,
      `${this.NURSE_API_ENDPOINT}/nurseResidentAssessments`,
      requestBody,
    );
  }

  public async updateNurseResidentAssessmentDocument(requestBody: {
    residentId: string;
    documentId: string;
    jobId?: string | null;
    jobStatus?: string | null;
    extractedOutputKey?: string | null;
    tag?: string;
    tags?: string[];
  }) {
    return await this.invokeApi(
      HTTPMethod.PUT,
      `${this.NURSE_API_ENDPOINT}/nurseResidentAssessments`,
      requestBody,
    );
  }

  public async deleteNurseResidentAssessmentDocument(residentId: string, documentId: string) {
    return await this.invokeApi(
      HTTPMethod.DELETE,
      `${this.NURSE_API_ENDPOINT}/nurseResidentAssessments?residentId=${residentId}&documentId=${documentId}`,
    );
  }

  public async getNurseAssessmentPresignedUrl(residentId: string, documentId: string, fileExtension: string) {
    return await this.invokeApi(
      HTTPMethod.POST,
      `${this.NURSE_API_ENDPOINT}/nurseAssessmentPresignedUrl`,
      { residentId, documentId, fileExtension },
    );
  }

  public async getProviderAssessmentPresignedUrl(residentId: string, documentId: string, fileExtension: string) {
    return await this.invokeApi(
      HTTPMethod.POST,
      `${this.RESIDENT_API_ENDPOINT}/residentAssessmentPresignedUrl`,
      { residentId, documentId, fileExtension },
    );
  }

  // ── Nurse Assessment Forms (structured stepper) ───────────────────────────

  public async getNurseAssessmentForms(residentId: string) {
    return await this.invokeApi(
      HTTPMethod.GET,
      `${this.NURSE_API_ENDPOINT}/nurseAssessmentForms?residentId=${residentId}`,
    );
  }

  public async getProviderAssessmentForms(residentId: string) {
    return await this.invokeApi(
      HTTPMethod.GET,
      `${this.RESIDENT_API_ENDPOINT}/residentAssessment?forms=true&residentId=${residentId}`,
    );
  }

  /** Returns all records (HEADER + STEP#xx items) for a specific form. */
  public async getNurseAssessmentFormSteps(formId: string) {
    return await this.invokeApi(
      HTTPMethod.GET,
      `${this.NURSE_API_ENDPOINT}/nurseAssessmentForms?formId=${formId}`,
    );
  }

  public async getProviderAssessmentFormSteps(formId: string) {
    return await this.invokeApi(
      HTTPMethod.GET,
      `${this.RESIDENT_API_ENDPOINT}/residentAssessment?forms=true&formId=${formId}`,
    );
  }

  public async createNurseAssessmentForm(residentId: string) {
    return await this.invokeApi(
      HTTPMethod.POST,
      `${this.NURSE_API_ENDPOINT}/nurseAssessmentForms`,
      { residentId },
    );
  }

  public async createProviderAssessmentForm(residentId: string) {
    return await this.invokeApi(
      HTTPMethod.POST,
      `${this.RESIDENT_API_ENDPOINT}/residentAssessment?forms=true`,
      { residentId },
    );
  }

  public async updateNurseAssessmentForm(requestBody: {
    formId: string;
    stepNumber?: number;
    stepData?: any;
    status?: string;
  }) {
    return await this.invokeApi(
      HTTPMethod.PUT,
      `${this.NURSE_API_ENDPOINT}/nurseAssessmentForms`,
      requestBody,
    );
  }

  public async updateProviderAssessmentForm(requestBody: {
    formId: string;
    stepNumber?: number;
    stepData?: any;
    status?: string;
  }) {
    return await this.invokeApi(
      HTTPMethod.PUT,
      `${this.RESIDENT_API_ENDPOINT}/residentAssessment?forms=true`,
      requestBody,
    );
  }

  public async deleteNurseAssessmentForm(formId: string) {
    return await this.invokeApi(
      HTTPMethod.DELETE,
      `${this.NURSE_API_ENDPOINT}/nurseAssessmentForms?formId=${formId}`,
    );
  }

  public async deleteProviderAssessmentForm(formId: string) {
    return await this.invokeApi(
      HTTPMethod.DELETE,
      `${this.RESIDENT_API_ENDPOINT}/residentAssessment?forms=true&formId=${formId}`,
    );
  }

  public async generateNurseAssessmentDocument(residentId: string, formId?: string) {
    const formQuery = formId ? `&formId=${encodeURIComponent(formId)}` : "";
    return await this.invokeApi(
      HTTPMethod.GET,
      `${this.NURSE_API_ENDPOINT}/nurseAssessmentForms?residentId=${encodeURIComponent(residentId)}${formQuery}&download=true`,
    );
  }

  public async generateNurseAssessmentMarSheet(residentId: string, formId?: string) {
    const formQuery = formId ? `&formId=${encodeURIComponent(formId)}` : "";
    return await this.invokeApi(
      HTTPMethod.GET,
      `${this.NURSE_API_ENDPOINT}/nurseAssessmentForms?residentId=${encodeURIComponent(residentId)}${formQuery}&downloadMarSheet=true`,
    );
  }

  public async generateProviderAssessmentDocument(residentId: string, formId?: string) {
    const formQuery = formId ? `&formId=${encodeURIComponent(formId)}` : "";
    return await this.invokeApi(
      HTTPMethod.GET,
      `${this.RESIDENT_API_ENDPOINT}/residentAssessment?forms=true&residentId=${encodeURIComponent(residentId)}${formQuery}&download=true`,
    );
  }

  public async getNurseDelegationForms(residentId: string, formCode?: string) {
    const formCodeQuery = formCode ? `&formCode=${encodeURIComponent(formCode)}` : "";
    return await this.invokeApi(
      HTTPMethod.GET,
      `${this.NURSE_API_ENDPOINT}/nurseDelegationForms?residentId=${encodeURIComponent(residentId)}${formCodeQuery}`,
    );
  }

  public async getNurseDelegationStaff(residentId: string) {
    return await this.invokeApi(
      HTTPMethod.GET,
      `${this.NURSE_API_ENDPOINT}/nurseDelegationForms?residentId=${encodeURIComponent(residentId)}&staff=true`,
    );
  }

  public async getProviderDelegationStaff(residentId: string) {
    return await this.invokeApi(
      HTTPMethod.GET,
      `${this.RESIDENT_API_ENDPOINT}/residentDelegation?residentId=${encodeURIComponent(residentId)}&staff=true`,
    );
  }

  public async getNurseDelegationTasks(residentId: string) {
    return await this.invokeApi(
      HTTPMethod.GET,
      `${this.NURSE_API_ENDPOINT}/nurseDelegationForms?residentId=${encodeURIComponent(residentId)}&tasks=true`,
    );
  }

  public async getProviderDelegationTasks(residentId: string) {
    return await this.invokeApi(
      HTTPMethod.GET,
      `${this.RESIDENT_API_ENDPOINT}/residentDelegation?residentId=${encodeURIComponent(residentId)}&tasks=true`,
    );
  }

  public async saveNurseDelegationTask(requestBody: { residentId: string; taskId?: string; [key: string]: any }) {
    return await this.invokeApi(HTTPMethod.PUT, `${this.NURSE_API_ENDPOINT}/nurseDelegationForms`, {
      ...requestBody,
      taskManagement: true,
    });
  }

  public async saveProviderDelegationTask(requestBody: { residentId: string; taskId?: string; [key: string]: any }) {
    return await this.invokeApi(HTTPMethod.PUT, `${this.RESIDENT_API_ENDPOINT}/residentDelegation`, {
      ...requestBody,
      taskManagement: true,
    });
  }

  public async getProviderDelegationForms(residentId: string, formCode?: string) {
    const formCodeQuery = formCode ? `&formCode=${encodeURIComponent(formCode)}` : "";
    return await this.invokeApi(
      HTTPMethod.GET,
      `${this.RESIDENT_API_ENDPOINT}/residentDelegation?residentId=${encodeURIComponent(residentId)}${formCodeQuery}`,
    );
  }

  public async getNurseDelegationFormRecords(formId: string) {
    return await this.invokeApi(
      HTTPMethod.GET,
      `${this.NURSE_API_ENDPOINT}/nurseDelegationForms?formId=${encodeURIComponent(formId)}`,
    );
  }

  public async generateNurseDelegationDocument(formId: string) {
    return await this.invokeApi(
      HTTPMethod.GET,
      `${this.NURSE_API_ENDPOINT}/nurseDelegationForms?formId=${encodeURIComponent(formId)}&download=true`,
    );
  }

  public async getProviderDelegationFormRecords(formId: string) {
    return await this.invokeApi(
      HTTPMethod.GET,
      `${this.RESIDENT_API_ENDPOINT}/residentDelegation?formId=${encodeURIComponent(formId)}`,
    );
  }

  public async generateProviderDelegationDocument(formId: string) {
    return await this.invokeApi(
      HTTPMethod.GET,
      `${this.RESIDENT_API_ENDPOINT}/residentDelegation?formId=${encodeURIComponent(formId)}&download=true`,
    );
  }

  public async createNurseDelegationForm(
    residentId: string,
    formCode: string,
    formTitle?: string,
    options?: {
      formThreadId?: string;
      formThreadLabel?: string;
      templateVersion?: string;
      templateSchemaVersion?: number;
      templateRevisionLabel?: string;
      templateDefinition?: any;
      staffIds?: string[];
      delegationTask?: any;
    },
  ) {
    return await this.invokeApi(
      HTTPMethod.POST,
      `${this.NURSE_API_ENDPOINT}/nurseDelegationForms`,
      { residentId, formCode, formTitle, ...options },
    );
  }

  public async generateProviderAssessmentMarSheet(residentId: string, formId?: string) {
    const formQuery = formId ? `&formId=${encodeURIComponent(formId)}` : "";
    return await this.invokeApi(
      HTTPMethod.GET,
      `${this.RESIDENT_API_ENDPOINT}/residentAssessment?forms=true&residentId=${encodeURIComponent(residentId)}${formQuery}&downloadMarSheet=true`,
    );
  }

  public async createProviderDelegationForm(
    residentId: string,
    formCode: string,
    formTitle?: string,
    options?: {
      formThreadId?: string;
      formThreadLabel?: string;
      templateVersion?: string;
      templateSchemaVersion?: number;
      templateRevisionLabel?: string;
      templateDefinition?: any;
      staffIds?: string[];
      delegationTask?: any;
    },
  ) {
    return await this.invokeApi(
      HTTPMethod.POST,
      `${this.RESIDENT_API_ENDPOINT}/residentDelegation`,
      { residentId, formCode, formTitle, ...options },
    );
  }

  public async createProviderDelegationDraftsFromMedicationSource(payload: {
    residentId: string;
    sourceType: "carePlan" | "ncp";
    sourceLabel?: string;
    medicationEntries: any[];
    staffIds?: string[];
    sourceFormId?: string;
    formId?: string;
    forceRegenerate?: boolean;
    useExistingCredit?: boolean;
  }) {
    return await this.invokeApi(
      HTTPMethod.POST,
      `${this.RESIDENT_API_ENDPOINT}/residentDelegation`,
      {
        createDraftsFromMedicationSource: true,
        ...payload,
      },
    );
  }

  public async createNurseDelegationDraftsFromMedicationSource(payload: {
    residentId: string;
    sourceType: "carePlan" | "ncp";
    sourceLabel?: string;
    medicationEntries: any[];
    staffIds?: string[];
    sourceFormId?: string;
    formId?: string;
    forceRegenerate?: boolean;
    useExistingCredit?: boolean;
  }) {
    return await this.invokeApi(
      HTTPMethod.POST,
      `${this.NURSE_API_ENDPOINT}/nurseDelegationForms`,
      {
        createDraftsFromMedicationSource: true,
        ...payload,
      },
    );
  }

  public async updateNurseDelegationForm(requestBody: {
    formId: string;
    sectionKey?: string;
    sectionData?: any;
    status?: string;
    formThreadLabel?: string;
  }) {
    return await this.invokeApi(
      HTTPMethod.PUT,
      `${this.NURSE_API_ENDPOINT}/nurseDelegationForms`,
      requestBody,
    );
  }

  public async updateProviderDelegationForm(requestBody: {
    formId: string;
    sectionKey?: string;
    sectionData?: any;
    status?: string;
    formThreadLabel?: string;
  }) {
    return await this.invokeApi(
      HTTPMethod.PUT,
      `${this.RESIDENT_API_ENDPOINT}/residentDelegation`,
      requestBody,
    );
  }

  public async deleteNurseDelegationForm(formId: string) {
    return await this.invokeApi(
      HTTPMethod.DELETE,
      `${this.NURSE_API_ENDPOINT}/nurseDelegationForms?formId=${encodeURIComponent(formId)}`,
    );
  }

  public async saveProviderDelegationStaff(requestBody: {
    residentId: string;
    staffId: string;
    name: string;
    email: string;
    [key: string]: any;
  }) {
    return await this.invokeApi(
      HTTPMethod.PUT,
      `${this.RESIDENT_API_ENDPOINT}/residentDelegation`,
      { ...requestBody, staffManagement: true },
    );
  }

  public async deleteAllNurseDelegationForms(residentId: string) {
    return await this.invokeApi(
      HTTPMethod.DELETE,
      `${this.NURSE_API_ENDPOINT}/nurseDelegationForms?residentId=${encodeURIComponent(residentId)}&deleteAll=true`,
    );
  }

  public async deleteProviderDelegationForm(formId: string) {
    return await this.invokeApi(
      HTTPMethod.DELETE,
      `${this.RESIDENT_API_ENDPOINT}/residentDelegation?formId=${encodeURIComponent(formId)}`,
    );
  }

  public async deleteAllProviderDelegationForms(residentId: string) {
    return await this.invokeApi(
      HTTPMethod.DELETE,
      `${this.RESIDENT_API_ENDPOINT}/residentDelegation?residentId=${encodeURIComponent(residentId)}&deleteAll=true`,
    );
  }

  public async getNursePodiatristForms(residentId: string) {
    return await this.invokeApi(
      HTTPMethod.GET,
      `${this.NURSE_API_ENDPOINT}/nurseFootNailCareForms?residentId=${encodeURIComponent(residentId)}`,
    );
  }

  public async getNursePodiatristFormRecords(formId: string) {
    return await this.invokeApi(
      HTTPMethod.GET,
      `${this.NURSE_API_ENDPOINT}/nurseFootNailCareForms?formId=${encodeURIComponent(formId)}`,
    );
  }

  public async generateNursePodiatristDocument(formId: string) {
    return await this.invokeApi(
      HTTPMethod.GET,
      `${this.NURSE_API_ENDPOINT}/nurseFootNailCareForms?formId=${encodeURIComponent(formId)}&download=true`,
    );
  }

  public async createNursePodiatristForms(payload: {
    residentId: string;
    generateFromCarePlan?: boolean;
    createBlankForm?: boolean;
    formCode?: string;
    copyFromFormId?: string;
  }) {
    return await this.invokeApi(
      HTTPMethod.POST,
      `${this.NURSE_API_ENDPOINT}/nurseFootNailCareForms`,
      payload,
    );
  }

  public async updateNursePodiatristForm(requestBody: {
    formId: string;
    formData?: any;
    status?: string;
  }) {
    return await this.invokeApi(
      HTTPMethod.PUT,
      `${this.NURSE_API_ENDPOINT}/nurseFootNailCareForms`,
      requestBody,
    );
  }

  public async getNurseNcpForms(residentId?: string, formId?: string) {
    const params = new URLSearchParams();
    if (residentId) {
      params.set("residentId", residentId);
    }
    if (formId) {
      params.set("formId", formId);
    }
    const query = params.toString() ? `?${params.toString()}` : "";
    return await this.invokeApi(
      HTTPMethod.GET,
      `${this.NURSE_API_ENDPOINT}/nurseNcpForms${query}`,
    );
  }

  public async createNurseNcpForm(residentId: string) {
    return await this.invokeApi(
      HTTPMethod.POST,
      `${this.NURSE_API_ENDPOINT}/nurseNcpForms`,
      { residentId },
    );
  }

  public async updateNurseNcpForm(requestBody: {
    formId: string;
    carePlanType?: string;
    sectionData?: any;
    status?: string;
  }) {
    return await this.invokeApi(
      HTTPMethod.PUT,
      `${this.NURSE_API_ENDPOINT}/nurseNcpForms`,
      requestBody,
    );
  }

  public async deleteNurseNcpForm(formId: string) {
    return await this.invokeApi(
      HTTPMethod.DELETE,
      `${this.NURSE_API_ENDPOINT}/nurseNcpForms?formId=${encodeURIComponent(formId)}`,
    );
  }

  public async generateNurseNcpDocument(residentId?: string, formId?: string) {
    const params = new URLSearchParams();
    params.set("download", "true");
    if (residentId) {
      params.set("residentId", residentId);
    }
    if (formId) {
      params.set("formId", formId);
    }
    return await this.invokeApi(
      HTTPMethod.GET,
      `${this.NURSE_API_ENDPOINT}/nurseNcpForms?${params.toString()}`,
    );
  }

  public async generateNursePreliminaryServicePlanDocument(residentId?: string, formId?: string) {
    const params = new URLSearchParams();
    params.set("generatePreliminaryServicePlanDocument", "true");
    if (residentId) {
      params.set("residentId", residentId);
    }
    if (formId) {
      params.set("formId", formId);
    }
    return await this.invokeApi(
      HTTPMethod.GET,
      `${this.NURSE_API_ENDPOINT}/nurseNcpForms?${params.toString()}`,
    );
  }

  public async generateNurseBehaviorInterventionsDocument(residentId?: string, formId?: string) {
    const params = new URLSearchParams();
    params.set("generateBehaviorInterventionsDocument", "true");
    if (residentId) {
      params.set("residentId", residentId);
    }
    if (formId) {
      params.set("formId", formId);
    }
    return await this.invokeApi(
      HTTPMethod.GET,
      `${this.NURSE_API_ENDPOINT}/nurseNcpForms?${params.toString()}`,
    );
  }

  public async generateNurseNcpMarSheet(residentId?: string, formId?: string) {
    const params = new URLSearchParams();
    params.set("downloadMarSheet", "true");
    if (residentId) {
      params.set("residentId", residentId);
    }
    if (formId) {
      params.set("formId", formId);
    }
    return await this.invokeApi(
      HTTPMethod.GET,
      `${this.NURSE_API_ENDPOINT}/nurseNcpForms?${params.toString()}`,
    );
  }

  public async startNurseNcpImportJob(requestBody: {
    residentId: string;
    formId: string;
    objectKey: string;
    billingRoleType?: string;
    useExistingCredit?: boolean;
  }) {
    return await this.addJobDetails({
      ...requestBody,
      referenceId: requestBody.residentId,
      jobType: "NURSE_NCP",
    });
  }

  public async getProviderResidentProfile(residentId: string) {
    return await this.invokeApi(
      HTTPMethod.GET,
      `${this.RESIDENT_API_ENDPOINT}/resident?residentId=${residentId}`,
    );
  }

  public async getActorLinks(status?: string, roleType?: string) {
    const params = new URLSearchParams();
    if (status) {
      params.set("status", status);
    }
    if (roleType) {
      params.set("roleType", roleType);
    }

    const query = params.toString();
    return await this.invokeApi(
      HTTPMethod.GET,
      `${this.RELATIONSHIP_API_ENDPOINT}/actorLinks${query ? `?${query}` : ""}`,
    );
  }

  public async searchActors(options?: {
    q?: string;
    email?: string;
    emails?: string[];
    roleType?: string;
    roleTypes?: string[];
  }) {
    const params = new URLSearchParams();
    const emailList: string[] = Array.isArray(options?.emails) ? [...(options?.emails as string[])] : [];
    const roleTypeList: string[] = Array.isArray(options?.roleTypes) ? [...(options?.roleTypes as string[])] : [];
    if (options?.q) {
      params.set("q", options.q);
    }
    if (options?.email) {
      params.set("email", options.email);
    }
    if (emailList.length > 0) {
      params.set("emails", emailList.join(","));
    }
    if (options?.roleType) {
      params.set("roleType", options.roleType);
    }
    if (roleTypeList.length > 0) {
      params.set("roleTypes", roleTypeList.join(","));
    }

    const query = params.toString();
    return await this.invokeApi(
      HTTPMethod.GET,
      `${this.RELATIONSHIP_API_ENDPOINT}/actors${query ? `?${query}` : ""}`,
    );
  }

  public async createActorLink(requestBody: {
    targetActorId: string;
    targetRoleType: string;
    relationshipType: string;
    directionMode?: string;
    message?: string;
    metadata?: Record<string, any>;
  }) {
    return await this.invokeApi(
      HTTPMethod.POST,
      `${this.RELATIONSHIP_API_ENDPOINT}/actorLinks`,
      requestBody,
    );
  }

  public async updateActorLinkStatus(requestBody: {
    linkId: string;
    status: string;
    message?: string;
    metadata?: Record<string, any>;
  }) {
    return await this.invokeApi(
      HTTPMethod.PUT,
      `${this.RELATIONSHIP_API_ENDPOINT}/actorLinks`,
      requestBody,
    );
  }

  public async getResidentRoleLinks(options?: {
    linkId?: string;
    providerResidentId?: string;
    actorLinkId?: string;
    status?: string;
    roleType?: string;
  }) {
    const params = new URLSearchParams();
    if (options?.linkId) {
      params.set("linkId", options.linkId);
    }
    if (options?.providerResidentId) {
      params.set("providerResidentId", options.providerResidentId);
    }
    if (options?.actorLinkId) {
      params.set("actorLinkId", options.actorLinkId);
    }
    if (options?.status) {
      params.set("status", options.status);
    }
    if (options?.roleType) {
      params.set("roleType", options.roleType);
    }

    const query = params.toString();
    return await this.invokeApi(
      HTTPMethod.GET,
      `${this.RELATIONSHIP_API_ENDPOINT}/residentRoleLinks${query ? `?${query}` : ""}`,
    );
  }

  public async createResidentRoleLink(requestBody: {
    actorLinkId?: string;
    providerResidentId: string;
    linkedActorId: string;
    roleType: string;
    assessmentAccessEnabled?: boolean;
    carePlanAccessEnabled?: boolean;
    delegationAccessEnabled?: boolean;
    binderAccessEnabled?: boolean;
    message?: string;
    metadata?: Record<string, any>;
  }) {
    return await this.invokeApi(
      HTTPMethod.POST,
      `${this.RELATIONSHIP_API_ENDPOINT}/residentRoleLinks`,
      requestBody,
    );
  }

  public async updateResidentRoleLinkStatus(requestBody: {
    linkId: string;
    status: string;
    roleResidentId?: string;
    assessmentAccessEnabled?: boolean;
    carePlanAccessEnabled?: boolean;
    delegationAccessEnabled?: boolean;
    binderAccessEnabled?: boolean;
    message?: string;
    metadata?: Record<string, any>;
  }) {
    return await this.invokeApi(
      HTTPMethod.PUT,
      `${this.RELATIONSHIP_API_ENDPOINT}/residentRoleLinks`,
      requestBody,
    );
  }

  public async disconnectSharedProvider(options: {
    providerHomeId?: string;
    providerActorId?: string;
    providerResidentId?: string;
    roleType: string;
  }) {
    const params = new URLSearchParams();
    if (options.providerHomeId) params.set("providerHomeId", options.providerHomeId);
    if (options.providerActorId) params.set("providerActorId", options.providerActorId);
    if (options.providerResidentId) params.set("providerResidentId", options.providerResidentId);
    params.set("roleType", options.roleType);
    return await this.invokeApi(
      HTTPMethod.DELETE,
      `${this.RELATIONSHIP_API_ENDPOINT}/residentRoleLinks?${params.toString()}`,
    );
  }

  public async getRelationshipNotifications(unreadOnly: boolean = false) {
    return await this.invokeApi(
      HTTPMethod.GET,
      `${this.RELATIONSHIP_API_ENDPOINT}/notifications?unreadOnly=${unreadOnly}`,
    );
  }

  public async getPlatformInvites(inviteId?: string) {
    const params = new URLSearchParams();
    if (inviteId) {
      params.set("inviteId", inviteId);
    }
    const query = params.toString() ? `?${params.toString()}` : "";
    return await this.invokeApi(
      HTTPMethod.GET,
      `${this.RELATIONSHIP_API_ENDPOINT}/invites${query}`,
    );
  }

  public async createPlatformInvite(requestBody: {
    targetEmail: string;
    targetName?: string;
    suggestedRoleType: string;
    message?: string;
    host?: string;
    brandOverride?: string;
  }) {
    return await this.invokeApi(
      HTTPMethod.POST,
      `${this.RELATIONSHIP_API_ENDPOINT}/invites`,
      requestBody,
    );
  }

  public async updatePlatformInviteStatus(requestBody: {
    inviteId: string;
    status: string;
  }) {
    return await this.invokeApi(
      HTTPMethod.PUT,
      `${this.RELATIONSHIP_API_ENDPOINT}/invites`,
      requestBody,
    );
  }

  public async updateRelationshipNotification(requestBody: {
    notificationId: string;
    markRead?: boolean;
  }) {
    return await this.invokeApi(
      HTTPMethod.PUT,
      `${this.RELATIONSHIP_API_ENDPOINT}/notifications`,
      requestBody,
    );
  }

  public async addCaregiverJobProfile(caregiverJobProfile: CaregiverJobProfile) {
    return await this.invokeApi(
      HTTPMethod.POST,
      `${this.CAREGIVER_API_ENDPOINT}/caregiverJob`,
      caregiverJobProfile,
    );
  }

  public async updateCaregiverJobProfile(caregiverJobProfile: CaregiverJobProfile) {
    return await this.invokeApi(
      HTTPMethod.PUT,
      `${this.CAREGIVER_API_ENDPOINT}/caregiverJob`,
      caregiverJobProfile,
    );
  }

  public async getCaregiverJobProfile(caregiverId: string) {
    return await this.invokeApi(
      HTTPMethod.GET,
      `${this.CAREGIVER_API_ENDPOINT}/caregiverJob?caregiverId=${caregiverId}`,
    );
  }

  public async getCaregiverResidents(residentId?: string) {
    const query = residentId ? `?residentId=${encodeURIComponent(residentId)}` : "";
    return await this.invokeApi(
      HTTPMethod.GET,
      `${this.CAREGIVER_API_ENDPOINT}/caregiverResidents${query}`,
    );
  }

  public async getCaregiverResidentSharesForHome(homeId: string, caregiverId?: string) {
    const params = new URLSearchParams({ homeId });
    if (caregiverId) {
      params.set("caregiverId", caregiverId);
    }

    return await this.invokeApi(
      HTTPMethod.GET,
      `${this.CAREGIVER_API_ENDPOINT}/caregiverResidents?${params.toString()}`,
    );
  }

  public async addCaregiverResidentShare(requestBody: {
    homeId: string;
    caregiverId: string;
    residentId: string;
    carePlanAccessEnabled?: boolean;
    behaviorLogAccessEnabled?: boolean;
    incidentReportsAccessEnabled?: boolean;
    dailyNotesAccessEnabled?: boolean;
    emarAccessEnabled?: boolean;
    adlAccessEnabled?: boolean;
  }) {
    return await this.invokeApi(
      HTTPMethod.POST,
      `${this.CAREGIVER_API_ENDPOINT}/caregiverResidents`,
      requestBody,
    );
  }

  public async bulkAssignCaregiverResidentShares(requestBody: {
    homeId: string;
    caregiverIds: string[];
    residentIds: string[];
    carePlanAccessEnabled?: boolean;
    behaviorLogAccessEnabled?: boolean;
    incidentReportsAccessEnabled?: boolean;
    dailyNotesAccessEnabled?: boolean;
    emarAccessEnabled?: boolean;
  }) {
    return await this.invokeApi(
      HTTPMethod.POST,
      `${this.CAREGIVER_API_ENDPOINT}/caregiverResidents`,
      {
        ...requestBody,
        bulkAssign: true,
      },
    );
  }

  public async deleteCaregiverResidentShare(homeId: string, caregiverId: string, residentId: string) {
    const params = new URLSearchParams({ homeId, caregiverId, residentId });
    return await this.invokeApi(
      HTTPMethod.DELETE,
      `${this.CAREGIVER_API_ENDPOINT}/caregiverResidents?${params.toString()}`,
    );
  }

  public async getCaregiverAttendance(request: {
    range?: "week" | "month";
    homeId?: string;
    reportMonth?: string;
  } = {}) {
    const params = new URLSearchParams();
    if (request.range) {
      params.set("range", request.range);
    }
    if (request.homeId) {
      params.set("homeId", request.homeId);
    }
    if (request.reportMonth) {
      params.set("reportMonth", request.reportMonth);
    }
    const query = params.toString();
    return await this.invokeApi(
      HTTPMethod.GET,
      `${this.CAREGIVER_API_ENDPOINT}/caregiverAttendance${query ? `?${query}` : ""}`,
    );
  }

  public async clockInCaregiverAttendance(requestBody: {
    homeId: string;
    homeName?: string;
    caregiverName?: string;
  }) {
    return await this.invokeApi(
      HTTPMethod.POST,
      `${this.CAREGIVER_API_ENDPOINT}/caregiverAttendance`,
      requestBody,
    );
  }

  public async generateCaregiverMonthlyTimePaySummaryDocument(requestBody: {
    homeId: string;
    caregiverId?: string;
    reportMonth?: string;
  }) {
    return await this.invokeApi(
      HTTPMethod.POST,
      `${this.CAREGIVER_API_ENDPOINT}/caregiverAttendance`,
      {
        ...requestBody,
        action: "generate_monthly_time_pay_summary_document",
      },
    );
  }

  public async updateHomeCaregiverLinkCompensation(requestBody: {
    homeId: string;
    caregiverId: string;
    linkId: string;
    hourlyRate: number;
    vacationBalanceHours: number;
    leaveBalanceHours: number;
    compensationEffectiveDate?: string;
    compensationNote?: string;
  }) {
    return await this.invokeApi(
      HTTPMethod.PUT,
      `${this.HOME_API_ENDPOINT}/homeCaregiverLinks`,
      requestBody,
    );
  }

  public async clockOutCaregiverAttendance() {
    return await this.invokeApi(
      HTTPMethod.PUT,
      `${this.CAREGIVER_API_ENDPOINT}/caregiverAttendance`,
      { action: "clock_out" },
    );
  }

  public async correctCaregiverAttendance(requestBody: {
    homeId: string;
    caregiverId: string;
    attendanceId: string;
    action: "clock_out_now" | "adjust_hours" | "edit_shift_times";
    correctionReason: string;
    correctionNote: string;
    correctedWorkedHours?: number;
    correctedClockInAtLocal?: string;
    correctedClockOutAtLocal?: string;
    previewOnly?: boolean;
  }) {
    return await this.invokeApi(
      HTTPMethod.PUT,
      `${this.CAREGIVER_API_ENDPOINT}/caregiverAttendance`,
      requestBody,
    );
  }

  public async updateProviderPayrollCloseout(requestBody: {
    homeId: string;
    range: "week" | "month";
    action: "finalize_payroll_period" | "reopen_payroll_period";
    approvalNote?: string;
    reportMonth?: string;
  }) {
    return await this.invokeApi(
      HTTPMethod.PUT,
      `${this.CAREGIVER_API_ENDPOINT}/caregiverAttendance`,
      requestBody,
    );
  }

  public async generateProviderPayrollPacketDocument(requestBody: {
    homeId: string;
    range: "week" | "month";
    reportMonth?: string;
  }) {
    return await this.invokeApi(
      HTTPMethod.POST,
      `${this.CAREGIVER_API_ENDPOINT}/caregiverAttendance`,
      {
        ...requestBody,
        action: "generate_payroll_packet_document",
      },
    );
  }

  public async updateProviderAttendanceReviewItem(requestBody: {
    homeId: string;
    reviewItemId: string;
    action: "resolve_review_item" | "reopen_review_item";
    resolutionNote?: string;
  }) {
    return await this.invokeApi(
      HTTPMethod.PUT,
      `${this.CAREGIVER_API_ENDPOINT}/caregiverAttendance`,
      requestBody,
    );
  }

  public async resendCaregiverAttendanceCorrectionEmail(requestBody: {
    homeId: string;
    caregiverId: string;
    attendanceId: string;
  }) {
    return await this.invokeApi(
      HTTPMethod.PUT,
      `${this.CAREGIVER_API_ENDPOINT}/caregiverAttendance`,
      {
        ...requestBody,
        action: "resend_correction_email",
      },
    );
  }

  public async getCaregiverResidentCarePlan(residentId: string, options?: { behaviorLogContext?: boolean }) {
    const params = new URLSearchParams({ residentId });
    if (options?.behaviorLogContext) {
      params.set("behaviorLogContext", "true");
    }
    return await this.invokeApi(
      HTTPMethod.GET,
      `${this.CAREGIVER_API_ENDPOINT}/caregiverResidentCarePlan?${params.toString()}`,
    );
  }

  public async getCaregiverResidentAdlTracking(residentId: string, options?: { historyDateKey?: string }) {
    const params = new URLSearchParams({ residentId });
    if (options?.historyDateKey) {
      params.set("historyDateKey", options.historyDateKey);
    }
    return await this.invokeApi(
      HTTPMethod.GET,
      `${this.CAREGIVER_API_ENDPOINT}/caregiverResidentCarePlan?${params.toString()}&adlTracking=true`,
    );
  }

  public async saveCaregiverResidentAdlEntry(requestBody: {
    residentId: string;
    dateKey: string;
    taskEntries: any[];
    correctionMode?: boolean;
    targetOccurrenceKey?: string;
    expectedVersion?: number;
    action?: string;
  }) {
    return await this.invokeApi(
      HTTPMethod.POST,
      `${this.CAREGIVER_API_ENDPOINT}/caregiverResidentCarePlan?adlTracking=true`,
      requestBody,
    );
  }

  public async getFamilyResidents(residentId?: string) {
    if (!this.FAMILY_API_ENDPOINT) {
      throw new Error("Family API endpoint is not configured.");
    }
    const query = residentId ? `?residentId=${encodeURIComponent(residentId)}` : "";
    return await this.invokeApi(
      HTTPMethod.GET,
      `${this.FAMILY_API_ENDPOINT}/familyResidents${query}`,
    );
  }

  public async getVendorProfile() {
    if (!this.VENDOR_API_ENDPOINT) {
      throw new Error("Vendor API endpoint is not configured.");
    }
    return await this.invokeApi(
      HTTPMethod.GET,
      `${this.VENDOR_API_ENDPOINT}/vendor`,
    );
  }

  public async addVendorProfile(requestBody: any) {
    if (!this.VENDOR_API_ENDPOINT) {
      throw new Error("Vendor API endpoint is not configured.");
    }
    return await this.invokeApi(
      HTTPMethod.POST,
      `${this.VENDOR_API_ENDPOINT}/vendor`,
      requestBody,
    );
  }

  public async updateVendorProfile(requestBody: any) {
    if (!this.VENDOR_API_ENDPOINT) {
      throw new Error("Vendor API endpoint is not configured.");
    }
    return await this.invokeApi(
      HTTPMethod.PUT,
      `${this.VENDOR_API_ENDPOINT}/vendor`,
      requestBody,
    );
  }

  public async searchCaregiverJobProfiles(caregiverJobProfileSearch: CaregiverJobProfileSearch) {
    return await this.invokeApi(
      HTTPMethod.POST,
      `${this.CAREGIVER_API_ENDPOINT}/caregiverJob?search=true`,
      caregiverJobProfileSearch
    );
  }

  public async addHomeHiringJobProfile(homeHiringJobProfile: HomeHiringJobProfile) {
    return await this.invokeApi(
      HTTPMethod.POST,
      `${this.HOME_API_ENDPOINT}/homeHiringProfile`,
      homeHiringJobProfile,
    );
  }

  public async updateHomeHiringJobProfile(homeHiringJobProfile: HomeHiringJobProfile) {
    return await this.invokeApi(
      HTTPMethod.PUT,
      `${this.HOME_API_ENDPOINT}/homeHiringProfile`,
      homeHiringJobProfile,
    );
  }

  public async getHomeHiringJobProfile(homeId: string) {
    return await this.invokeApi(
      HTTPMethod.GET,
      `${this.HOME_API_ENDPOINT}/homeHiringProfile?homeId=${homeId}`,
    );
  }

  public async searchHomeHiringJobProfiles(homeHiringJobProfileSearch: HomeHiringJobProfileSearch) {
    return await this.invokeApi(
      HTTPMethod.POST,
      `${this.HOME_API_ENDPOINT}/homeHiringProfile?search=true`,
      homeHiringJobProfileSearch
    );
  }

  public async getClientAvailabilityProfile(homeId: string) {
    return await this.invokeApi(
      HTTPMethod.GET,
      `${this.HOME_API_ENDPOINT}/clientAvailabilityProfile?homeId=${homeId}`,
    );
  }

  public async listAllClientAvailabilityProfilesForHome(homeId: string) {
    return await this.invokeApi(
      HTTPMethod.GET,
      `${this.HOME_API_ENDPOINT}/clientAvailabilityProfile?homeId=${homeId}`,
    );
  }

  public async updateClientAvailabilityProfile(clientAvailabilityProfile: ClientAvailabilityProfile) {
    return await this.invokeApi(
      HTTPMethod.PUT,
      `${this.HOME_API_ENDPOINT}/clientAvailabilityProfile`,
      clientAvailabilityProfile,
    );
  }

  public async addClientAvailabilityProfile(clientAvailabilityProfile: ClientAvailabilityProfile) {
    return await this.invokeApi(
      HTTPMethod.POST,
      `${this.HOME_API_ENDPOINT}/clientAvailabilityProfile`,
      clientAvailabilityProfile,
    );
  }

  public async searchListings(searchInput: ListingSearchInput) {
    return await this.invokeApi(
      HTTPMethod.POST,
      `${this.HOME_API_ENDPOINT}/listings`,
      searchInput
    );
  }

  public async getPresignedUrlForAssessmentUpload(
    residentId: string,
  ) {
    const requestBody = {
      residentId: residentId,
    };
    return await this.invokeApi(
      HTTPMethod.PUT,
      `${this.DOCUMENT_API_ENDPOINT}/presignedUrlForUpload?assessmentUpload=true`,
      requestBody,
    );
  }

  public async sendBinderDocumentFax(payload: { binderDocumentId: string; versionId?: string; faxNumber: string; }) {
    return await this.invokeApi(
      HTTPMethod.POST,
      `${this.DOCUMENT_API_ENDPOINT}/fax/send`,
      payload,
    );
  }

  public async listFaxAudit(scope: "me" | "admin" = "me", params: { status?: string; pageSize?: number; nextToken?: string } = {}) {
    const query = new URLSearchParams({ scope, pageSize: String(params.pageSize || 25) });
    if (params.status) query.set("status", params.status);
    if (params.nextToken) query.set("nextToken", params.nextToken);
    return await this.invokeApi(HTTPMethod.GET, `${this.DOCUMENT_API_ENDPOINT}/fax/audit?${query.toString()}`);
  }

  public async getFaxAuditSummary() {
    return await this.invokeApi(HTTPMethod.GET, `${this.DOCUMENT_API_ENDPOINT}/fax/audit/summary`);
  }

  public async getPresignedUrlForUpload(
    referenceId: string,
    referenceType: string,
    objectKey: string,
  ) {
    const requestBody = {
      referenceId: referenceId,
      referenceType: referenceType,
      objectKey: objectKey,
    };
    return await this.invokeApi(
      HTTPMethod.PUT,
      `${this.DOCUMENT_API_ENDPOINT}/presignedUrlForUpload`,
      requestBody,
    );
  }

  public async getPresignedUrlForAICompanionFileUpload(objectKey: string) {
    const requestBody = {
      objectKey: objectKey,
    };
    return await this.invokeApi(
      HTTPMethod.PUT,
      `${this.DOCUMENT_API_ENDPOINT}/presignedUrlForUpload?aiCompanionFile=true`,
      requestBody,
    );
  }

  public async getPresignedUrlForProfilePicUpload(userId: string) {
    await this.init();
    const requestBody = { userId: userId };
    return await this.invokeApi(
      HTTPMethod.PUT,
      `${this.DOCUMENT_API_ENDPOINT}/presignedUrlForUploadProfilePic`,
      requestBody,
    );
  }

  public async getPresignedUrlForProfilePicDownload(userId: string) {
    await this.init();
    const requestBody = { userId: userId };
    return await this.invokeApi(
      HTTPMethod.PUT,
      `${this.DOCUMENT_API_ENDPOINT}/presignedUrlForDownloadProfilePic`,
      requestBody,
    );
  }

  public async handlePresignedUrlForDownloadResidentPic(residentId: string) {
    await this.init();
    const requestBody = { residentId: residentId };
    return await this.invokeApi(
      HTTPMethod.PUT,
      `${this.DOCUMENT_API_ENDPOINT}/presignedUrlForDownloadResidentPic`,
      requestBody,
    );
  }

  public async handlePresignedUrlForUploadResidentPic(residentId: string) {
    await this.init();
    const requestBody = { residentId: residentId };
    return await this.invokeApi(
      HTTPMethod.PUT,
      `${this.DOCUMENT_API_ENDPOINT}/presignedUrlForUploadResidentPic`,
      requestBody,
    );
  }

  public async uploadFileToS3(preSignedUrl: string, file: File) {
    await axios.put(preSignedUrl, file, {
      headers: {
        "Content-Type": file.type,
      },
    });
  }

  public async getPresignedUrlForDownload(
    referenceId: string,
    referenceType: string,
    documentId: string,
    objectKey: string,
    fileName: string,
    inlinePreview: boolean = false,
  ) {
    const requestBody = {
      referenceId: referenceId,
      referenceType: referenceType,
      documentId: documentId,
      objectKey: objectKey,
      fileName: fileName,
      inlinePreview,
    };
    return await this.invokeApi(
      HTTPMethod.PUT,
      `${this.DOCUMENT_API_ENDPOINT}/presignedUrlForDownload`,
      requestBody,
    );
  }

  public async getResidentScopedJsonFromObjectKey(
    residentId: string,
    objectKey: string,
    fileName: string = "comparison-result.json",
  ) {
    const downloadUrl = await this.getPresignedUrlForDownload(
      residentId,
      "resident",
      "",
      objectKey,
      fileName,
      true,
    );
    const response = await axios.get(downloadUrl);
    return response.data;
  }

  public async listAllFoldersByReference(referenceId: string, referenceType: string) {
    return await this.invokeApi(
      HTTPMethod.GET,
      `${this.DOCUMENT_API_ENDPOINT}/folder?referenceId=${referenceId}&referenceType=${referenceType}`,
    );
  }

  public async addNewFolder(folder: DocumentFolder) {
    return await this.invokeApi(
      HTTPMethod.POST,
      `${this.DOCUMENT_API_ENDPOINT}/folder`,
      folder,
    );
  }

  public async deleteFolder(folderId: string, referenceId: string, referenceType: string) {
    return await this.invokeApi(
      HTTPMethod.DELETE,
      `${this.DOCUMENT_API_ENDPOINT}/folder?folderId=${folderId}&referenceId=${referenceId}&referenceType=${referenceType}`,
    );
  }

  public async shareFolderWithUser(sharedFolder: SharedFolder) {
    return await this.invokeApi(
      HTTPMethod.POST,
      `${this.DOCUMENT_API_ENDPOINT}/sharedFolder`,
      sharedFolder,
    );
  }

  public async listAllSharedUsersOfFolder(folderId: string, referenceId: string, referenceType: string) {
    return await this.invokeApi(
      HTTPMethod.GET,
      `${this.DOCUMENT_API_ENDPOINT}/sharedFolder?folderId=${folderId}&referenceId=${referenceId}&referenceType=${referenceType}`,
    );
  }

  public async listAllSharedFoldersWithMe() {
    return await this.invokeApi(
      HTTPMethod.GET,
      `${this.DOCUMENT_API_ENDPOINT}/sharedFolder`,
    );
  }

  public async deleteSharedFolder(folderId: string, id: string) {
    return await this.invokeApi(
      HTTPMethod.DELETE,
      `${this.DOCUMENT_API_ENDPOINT}/sharedFolder?folderId=${folderId}&id=${id}`,
    );
  }

  public async addDocument(document: Document) {
    return await this.invokeApi(
      HTTPMethod.POST,
      `${this.DOCUMENT_API_ENDPOINT}/document`,
      document,
    );
  }

  public async updateDocument(document: Document) {
    return await this.invokeApi(
      HTTPMethod.PUT,
      `${this.DOCUMENT_API_ENDPOINT}/document`,
      document,
    );
  }

  public async deleteDocument(document: Document) {
    return await this.invokeApi(
      HTTPMethod.DELETE,
      `${this.DOCUMENT_API_ENDPOINT}/document?documentId=${document.documentId}`,
    );
  }

  public async listAllDocument(referenceId: string, referenceType: string) {
    return await this.invokeApi(
      HTTPMethod.GET,
      `${this.DOCUMENT_API_ENDPOINT}/document?referenceId=${referenceId}&referenceType=${referenceType}`,
    );
  }

  public async listAllDocumentsForSharedFolder(folderId: string) {
    return await this.invokeApi(
      HTTPMethod.GET,
      `${this.DOCUMENT_API_ENDPOINT}/document?folderId=${folderId}`,
    );
  }

  public async getBinderByReference(referenceType: string, referenceId: string, facilityType?: string) {
    return await this.invokeApi(
      HTTPMethod.GET,
      `${this.BINDER_API_ENDPOINT}/binder?referenceType=${referenceType}&referenceId=${referenceId}${facilityType ? `&facilityType=${encodeURIComponent(facilityType)}` : ""}`,
    );
  }

  public async listBinderFacilityTypes() {
    const params = new URLSearchParams({ facilityTypes: "true" });
    if (typeof window !== "undefined" && window.location?.host) params.set("host", window.location.host);
    const brandOverride = getSessionBrandOverride();
    if (brandOverride) params.set("brandOverride", brandOverride);
    return await this.invokeApi(HTTPMethod.GET, `${this.BINDER_API_ENDPOINT}/binder?${params.toString()}`);
  }

  public async bootstrapBinder(requestBody: { binderType: string; referenceType: string; referenceId: string; facilityType?: string; }) {
    const params = new URLSearchParams({ bootstrap: "true" });
    if (typeof window !== "undefined" && window.location?.host) params.set("host", window.location.host);
    const brandOverride = getSessionBrandOverride();
    if (brandOverride) params.set("brandOverride", brandOverride);
    return await this.invokeApi(
      HTTPMethod.POST,
      `${this.BINDER_API_ENDPOINT}/binder?${params.toString()}`,
      requestBody,
    );
  }

  public async listBinderFolders(binderId: string) {
    return await this.invokeApi(
      HTTPMethod.GET,
      `${this.BINDER_API_ENDPOINT}/binderFolder?binderId=${binderId}`,
    );
  }

  public async createBinderFolder(folder: BinderFolder) {
    return await this.invokeApi(
      HTTPMethod.POST,
      `${this.BINDER_API_ENDPOINT}/binderFolder`,
      folder,
    );
  }

  public async resetBinderConfig(requestBody: { binderId: string; facilityType: string; }) {
    const params = new URLSearchParams({ resetConfig: "true" });
    if (typeof window !== "undefined" && window.location?.host) params.set("host", window.location.host);
    const brandOverride = getSessionBrandOverride();
    if (brandOverride) params.set("brandOverride", brandOverride);
    return await this.invokeApi(
      HTTPMethod.POST,
      `${this.BINDER_API_ENDPOINT}/binder?${params.toString()}`,
      requestBody,
    );
  }

  public async updateBinderFolder(folder: BinderFolder) {
    return await this.invokeApi(
      HTTPMethod.PUT,
      `${this.BINDER_API_ENDPOINT}/binderFolder`,
      folder,
    );
  }

  public async deleteBinderFolder(folderId: string) {
    return await this.invokeApi(
      HTTPMethod.DELETE,
      `${this.BINDER_API_ENDPOINT}/binderFolder?folderId=${folderId}`,
    );
  }

  public async listBinderDocuments(binderId: string, folderId?: string, shareId?: string) {
    const query = [
      `binderId=${encodeURIComponent(binderId)}`,
      folderId ? `folderId=${encodeURIComponent(folderId)}` : "",
      shareId ? `shareId=${encodeURIComponent(shareId)}` : "",
    ].filter(Boolean).join("&");
    return await this.invokeApi(
      HTTPMethod.GET,
      `${this.BINDER_API_ENDPOINT}/binderDocument?${query}`,
    );
  }

  public async createBinderDocument(document: BinderDocument) {
    return await this.invokeApi(
      HTTPMethod.POST,
      `${this.BINDER_API_ENDPOINT}/binderDocument`,
      document,
    );
  }

  public async updateBinderDocument(document: BinderDocument) {
    return await this.invokeApi(
      HTTPMethod.PUT,
      `${this.BINDER_API_ENDPOINT}/binderDocument`,
      document,
    );
  }

  public async deleteBinderDocument(binderDocumentId: string) {
    return await this.invokeApi(
      HTTPMethod.DELETE,
      `${this.BINDER_API_ENDPOINT}/binderDocument`,
      { binderDocumentId },
    );
  }

  public async listBinderDocumentVersions(binderDocumentId: string, shareId?: string) {
    return await this.invokeApi(
      HTTPMethod.GET,
      `${this.BINDER_API_ENDPOINT}/binderDocumentVersion?binderDocumentId=${encodeURIComponent(binderDocumentId)}${shareId ? `&shareId=${encodeURIComponent(shareId)}` : ""}`,
    );
  }

  public async createBinderDocumentVersion(version: BinderDocumentVersion) {
    return await this.invokeApi(
      HTTPMethod.POST,
      `${this.BINDER_API_ENDPOINT}/binderDocumentVersion`,
      version,
    );
  }

  public async listBinderDocumentAnalyses(binderDocumentId: string): Promise<BinderDocumentAnalysis[]> {
    return await this.invokeApi(
      HTTPMethod.GET,
      `${this.BINDER_API_ENDPOINT}/binderDocumentAnalysis?binderDocumentId=${binderDocumentId}`,
    );
  }

  public async listBinderAnalysesByBinderId(binderId: string, latestOnly = true): Promise<BinderDocumentAnalysis[]> {
    return await this.invokeApi(
      HTTPMethod.GET,
      `${this.BINDER_API_ENDPOINT}/binderDocumentAnalysis?binderId=${binderId}&latestOnly=${latestOnly ? "true" : "false"}`,
    );
  }

  public async runBinderDocumentAnalysis(binderDocumentId: string): Promise<BinderDocumentAnalysis> {
    return await this.invokeApi(
      HTTPMethod.POST,
      `${this.BINDER_API_ENDPOINT}/binderDocumentAnalysis`,
      { binderDocumentId },
    );
  }

  public async getBinderDocumentDownloadUrl(binderDocumentId: string, versionId?: string, shareId?: string) {
    return await this.invokeApi(
      HTTPMethod.POST,
      `${this.BINDER_API_ENDPOINT}/binderDocumentDownload`,
      { binderDocumentId, versionId, shareId },
    );
  }

  public async listBinderSharesByBinderId(binderId: string): Promise<BinderShare[]> {
    return await this.invokeApi(
      HTTPMethod.GET,
      `${this.BINDER_API_ENDPOINT}/binderShare?binderId=${encodeURIComponent(binderId)}`,
    );
  }

  public async listBinderSharesSharedWithMe(): Promise<BinderShare[]> {
    return await this.invokeApi(
      HTTPMethod.GET,
      `${this.BINDER_API_ENDPOINT}/binderShare?sharedWithMe=true`,
    );
  }

  public async getBinderShare(shareId: string): Promise<any> {
    return await this.invokeApi(
      HTTPMethod.GET,
      `${this.BINDER_API_ENDPOINT}/binderShare?shareId=${encodeURIComponent(shareId)}`,
    );
  }

  public async getBinderSharePacket(shareId: string): Promise<any> {
    return await this.invokeApi(
      HTTPMethod.GET,
      `${this.BINDER_API_ENDPOINT}/binderShare?shareId=${encodeURIComponent(shareId)}&packet=true`,
    );
  }

  public async createBinderShare(requestBody: BinderShare): Promise<BinderShare> {
    return await this.invokeApi(
      HTTPMethod.POST,
      `${this.BINDER_API_ENDPOINT}/binderShare`,
      {
        ...requestBody,
        brandOverride: this.getCurrentBrandOverride(),
      },
    );
  }

  public async updateBinderShare(requestBody: BinderShare): Promise<BinderShare> {
    return await this.invokeApi(
      HTTPMethod.PUT,
      `${this.BINDER_API_ENDPOINT}/binderShare`,
      {
        ...requestBody,
        brandOverride: this.getCurrentBrandOverride(),
      },
    );
  }

  public async deleteBinderShare(shareId: string) {
    return await this.invokeApi(
      HTTPMethod.DELETE,
      `${this.BINDER_API_ENDPOINT}/binderShare`,
      { shareId },
    );
  }

  public async listHiringRequests(): Promise<any[]> {
    return await this.invokeApi(HTTPMethod.GET, `${this.BINDER_API_ENDPOINT}/hiringRequest`);
  }

  public async getHiringRequest(requestId: string): Promise<any> {
    return await this.invokeApi(HTTPMethod.GET, `${this.BINDER_API_ENDPOINT}/hiringRequest?requestId=${encodeURIComponent(requestId)}`);
  }

  public async createHiringRequest(request: object): Promise<any> {
    return await this.invokeApi(HTTPMethod.POST, `${this.BINDER_API_ENDPOINT}/hiringRequest`, {
      ...request,
      brandOverride: this.getCurrentBrandOverride(),
    });
  }

  public async updateHiringRequest(request: object): Promise<any> {
    return await this.invokeApi(HTTPMethod.PUT, `${this.BINDER_API_ENDPOINT}/hiringRequest`, request);
  }

  public async runBinderCompliance(binderId: string) {
    return await this.invokeApi(
      HTTPMethod.POST,
      `${this.BINDER_API_ENDPOINT}/binderCompliance`,
      { binderId },
    );
  }

  public async listBinderComplianceRuns(binderId: string) {
    return await this.invokeApi(
      HTTPMethod.GET,
      `${this.BINDER_API_ENDPOINT}/binderCompliance?binderId=${binderId}`,
    );
  }

  public async listBinderRules(binderType?: string) {
    const query = binderType ? `?binderType=${binderType}` : "";
    return await this.invokeApi(
      HTTPMethod.GET,
      `${this.BINDER_API_ENDPOINT}/binderRule${query}`,
    );
  }

  public async createBinderRule(rule: BinderRule) {
    return await this.invokeApi(
      HTTPMethod.POST,
      `${this.BINDER_API_ENDPOINT}/binderRule`,
      rule,
    );
  }

  public async updateBinderRule(rule: BinderRule) {
    return await this.invokeApi(
      HTTPMethod.PUT,
      `${this.BINDER_API_ENDPOINT}/binderRule`,
      rule,
    );
  }

  public async retireBinderRule(ruleId: string) {
    return await this.invokeApi(
      HTTPMethod.DELETE,
      `${this.BINDER_API_ENDPOINT}/binderRule`,
      { ruleId },
    );
  }

  public async listBinderRuleSources(binderType?: string) {
    const query = binderType ? `?binderType=${binderType}` : "";
    return await this.invokeApi(
      HTTPMethod.GET,
      `${this.BINDER_API_ENDPOINT}/binderRuleSource${query}`,
    );
  }

  public async createBinderRuleSource(source: BinderRuleSource) {
    return await this.invokeApi(
      HTTPMethod.POST,
      `${this.BINDER_API_ENDPOINT}/binderRuleSource`,
      source,
    );
  }

  public async updateBinderRuleSource(source: BinderRuleSource) {
    return await this.invokeApi(
      HTTPMethod.PUT,
      `${this.BINDER_API_ENDPOINT}/binderRuleSource`,
      source,
    );
  }

  public async deleteBinderRuleSource(sourceId: string) {
    return await this.invokeApi(
      HTTPMethod.DELETE,
      `${this.BINDER_API_ENDPOINT}/binderRuleSource`,
      { sourceId },
    );
  }

  public async listBinderRuleChangeAlerts(binderType?: string) {
    const query = binderType ? `?binderType=${binderType}` : "";
    return await this.invokeApi(
      HTTPMethod.GET,
      `${this.BINDER_API_ENDPOINT}/binderRuleChangeAlert${query}`,
    );
  }

  public async updateBinderRuleChangeAlert(alert: BinderRuleChangeAlert) {
    return await this.invokeApi(
      HTTPMethod.PUT,
      `${this.BINDER_API_ENDPOINT}/binderRuleChangeAlert`,
      alert,
    );
  }

  public async listBinderRuleSnapshots(sourceId: string) {
    return await this.invokeApi(
      HTTPMethod.GET,
      `${this.BINDER_API_ENDPOINT}/binderRuleSnapshot?sourceId=${sourceId}`,
    );
  }

  public async getBinderRuleSnapshot(snapshotId: string): Promise<BinderRuleSnapshot> {
    return await this.invokeApi(
      HTTPMethod.GET,
      `${this.BINDER_API_ENDPOINT}/binderRuleSnapshot?snapshotId=${snapshotId}`,
    );
  }

  public async runBinderRuleMonitor(requestBody?: { binderType?: string; sourceId?: string }) {
    return await this.invokeApi(
      HTTPMethod.POST,
      `${this.BINDER_API_ENDPOINT}/binderRuleMonitor`,
      requestBody || {},
    );
  }

  public async getProviderDirectoryDashboard(runLimit = 10, changeLimit = 25, entryLimit = 50): Promise<ProviderDirectoryDashboard> {
    return await this.invokeApi(
      HTTPMethod.GET,
      `${this.PROVIDER_DIRECTORY_API_ENDPOINT}/providerDirectory?dashboard=true&runLimit=${runLimit}&changeLimit=${changeLimit}&entryLimit=${entryLimit}`,
    );
  }

  public async listProviderDirectoryRuns(limit = 10): Promise<ProviderDirectoryRun[]> {
    return await this.invokeApi(
      HTTPMethod.GET,
      `${this.PROVIDER_DIRECTORY_API_ENDPOINT}/providerDirectory?listRuns=true&limit=${limit}`,
    );
  }

  public async listProviderDirectoryCurrentEntriesPage(params?: {
    limit?: number;
    nextToken?: string;
    search?: string;
    facilityPOC?: string;
    telephoneNumber?: string;
    speciality?: string;
    formattedAddress?: string;
    licensedBedCount?: string;
    hasReports?: string;
  }): Promise<ProviderDirectoryCurrentEntriesPage> {
    const searchParams = new URLSearchParams();
    searchParams.set("listCurrent", "true");
    searchParams.set("limit", String(params?.limit || 100));
    if (params?.nextToken) {
      searchParams.set("nextToken", params.nextToken);
    }
    if (params?.search) {
      searchParams.set("search", params.search);
    }
    if (params?.facilityPOC) {
      searchParams.set("facilityPOC", params.facilityPOC);
    }
    if (params?.telephoneNumber) {
      searchParams.set("telephoneNumber", params.telephoneNumber);
    }
    if (params?.speciality) {
      searchParams.set("speciality", params.speciality);
    }
    if (params?.formattedAddress) {
      searchParams.set("formattedAddress", params.formattedAddress);
    }
    if (params?.licensedBedCount) {
      searchParams.set("licensedBedCount", params.licensedBedCount);
    }
    if (params?.hasReports) {
      searchParams.set("hasReports", params.hasReports);
    }

    return await this.invokeApi(
      HTTPMethod.GET,
      `${this.PROVIDER_DIRECTORY_API_ENDPOINT}/providerDirectory?${searchParams.toString()}`,
    );
  }

  public async exportProviderDirectoryCurrentEntries(): Promise<ProviderDirectoryExportResult> {
    return await this.invokeApi(
      HTTPMethod.GET,
      `${this.PROVIDER_DIRECTORY_API_ENDPOINT}/providerDirectory?exportCurrent=true`,
    );
  }

  public async startProviderDirectoryImport() {
    return await this.invokeApi(
      HTTPMethod.POST,
      `${this.PROVIDER_DIRECTORY_API_ENDPOINT}/providerDirectory`,
      { startImport: true },
    );
  }

  public async startNurseDirectoryImport() {
    return await this.invokeApi(
      HTTPMethod.POST,
      `${this.PROVIDER_DIRECTORY_API_ENDPOINT}/providerDirectory`,
      { startNurseImport: true },
    );
  }

  public async startProviderDirectoryReportSync() {
    return await this.invokeApi(
      HTTPMethod.POST,
      `${this.PROVIDER_DIRECTORY_API_ENDPOINT}/providerDirectory`,
      { startReportSync: true },
    );
  }

  public async listProviderDirectoryReportRuns(limit = 10): Promise<ProviderDirectoryReportRun[]> {
    return await this.invokeApi(
      HTTPMethod.GET,
      `${this.PROVIDER_DIRECTORY_API_ENDPOINT}/providerDirectory?listReportRuns=true&limit=${limit}`,
    );
  }

  public async listProviderDirectoryReportChanges(limit = 25): Promise<ProviderDirectoryReportChange[]> {
    return await this.invokeApi(
      HTTPMethod.GET,
      `${this.PROVIDER_DIRECTORY_API_ENDPOINT}/providerDirectory?listReportChanges=true&limit=${limit}`,
    );
  }

  public async listProviderDirectoryReportDocuments(providerId: string, limit = 50): Promise<ProviderDirectoryReportDocument[]> {
    return await this.invokeApi(
      HTTPMethod.GET,
      `${this.PROVIDER_DIRECTORY_API_ENDPOINT}/providerDirectory?listReportDocuments=true&providerId=${encodeURIComponent(providerId)}&limit=${encodeURIComponent(String(limit))}`,
    );
  }

  public async searchProviderDirectoryMap(
    latitude: number,
    longitude: number,
    radiusInMiles: number,
    limit: number = 200,
  ): Promise<ProviderDirectoryMapSearchResult> {
    return await this.invokeApi(
      HTTPMethod.GET,
      `${this.PROVIDER_DIRECTORY_API_ENDPOINT}/providerDirectory?mapSearch=true&latitude=${encodeURIComponent(String(latitude))}&longitude=${encodeURIComponent(String(longitude))}&radiusInMiles=${encodeURIComponent(String(radiusInMiles))}&limit=${encodeURIComponent(String(limit))}`,
    );
  }

  public async getBlobFromURL(url: string) {
    return await axios.get(url, { responseType: "blob" });
  }

  public async getResidentsForHome(homeId: string, activeOnly: boolean = false) {
    const params = new URLSearchParams({ homeId });
    if (activeOnly) {
      params.set("activeOnly", "true");
    }
    return await this.invokeApi(
      HTTPMethod.GET,
      `${this.RESIDENT_API_ENDPOINT}/resident?${params.toString()}`,
    );
  }

  public async getResidentDetails(residentId: string) {
    return await this.invokeApi(
      HTTPMethod.GET,
      `${this.RESIDENT_API_ENDPOINT}/resident?residentId=${residentId}`,
    );
  }

  public async getResidentSwitcherResidents(residentId: string, featureKey?: string) {
    const params = new URLSearchParams({ residentId });
    if (featureKey) {
      params.set("featureKey", featureKey);
    }
    return await this.invokeApi(
      HTTPMethod.GET,
      `${this.RESIDENT_API_ENDPOINT}/residentSwitcher?${params.toString()}`,
    );
  }

  public async getResidentCountsCreatedBy() {
    return await this.invokeApi(
      HTTPMethod.GET,
      `${this.RESIDENT_API_ENDPOINT}/resident?createdBy=true`,
    );
  }

  public async addResident(resident: ResidentProfile) {
    return await this.invokeApi(
      HTTPMethod.POST,
      `${this.RESIDENT_API_ENDPOINT}/resident`,
      resident,
    );
  }

  public async admitIntakeSubmission(intakeSubmissionId: string) {
    return await this.invokeApi(
      HTTPMethod.POST,
      `${this.RESIDENT_API_ENDPOINT}/resident`,
      { intakeSubmissionId },
    );
  }

  public async resetIntakeSubmissionAdmission(intakeSubmissionId: string) {
    return await this.invokeApi(
      HTTPMethod.DELETE,
      `${this.RESIDENT_API_ENDPOINT}/resident?intakeSubmissionId=${encodeURIComponent(intakeSubmissionId)}`,
    );
  }

  public async updateResident(resident: ResidentProfile) {
    return await this.invokeApi(
      HTTPMethod.PUT,
      `${this.RESIDENT_API_ENDPOINT}/resident`,
      resident,
    );
  }

  public async syncResidentClinicalContactsFromCarePlan(residentId: string, forceOverwrite = false) {
    return await this.invokeApi(
      HTTPMethod.PUT,
      `${this.RESIDENT_API_ENDPOINT}/resident`,
      {
        residentId,
        forceOverwrite,
        syncClinicalContactsFromCarePlan: true,
      },
    );
  }

  public async syncResidentMedicationDiagnosisFromCarePlan(residentId: string, forceOverwrite = false) {
    return await this.invokeApi(
      HTTPMethod.PUT,
      `${this.RESIDENT_API_ENDPOINT}/resident`,
      {
        residentId,
        forceOverwrite,
        syncMedicationDiagnosisFromCarePlan: true,
      },
    );
  }

  public async deleteResident(residentId: string) {
    return await this.invokeApi(
      HTTPMethod.DELETE,
      `${this.RESIDENT_API_ENDPOINT}/resident?residentId=${residentId}`,
    );
  }

  public async deactivateResident(residentId: string) {
    return await this.invokeApi(
      HTTPMethod.DELETE,
      `${this.RESIDENT_API_ENDPOINT}/resident?residentId=${residentId}&deactivate=true`,
    );
  }

  public async updateResidentCarePlan(residentCarePlanData: any) {
    return await this.invokeApi(
      HTTPMethod.PUT,
      `${this.RESIDENT_API_ENDPOINT}/residentCarePlan`,
      residentCarePlanData,
    );
  }

  public async generateResidentCarePlanDocument(residentId: string, homeId: string) {
    const requestBody = {
      residentId: residentId,
      homeId: homeId,
    };
    return await this.invokeApi(
      HTTPMethod.POST,
      `${this.RESIDENT_API_ENDPOINT}/residentCarePlan`,
      requestBody,
    );
  }

  public async applyBehaviorInterventionImportToCarePlan(payload: {
    residentId: string;
    homeId: string;
    jobId: string;
    selectedMatches?: Array<{
      behaviorLabel: string;
      behaviorDetails?: string;
      interventionDetails?: string;
      actionToTake?: string;
      selected?: boolean;
      matchId?: string;
    }>;
  }) {
    return await this.invokeApi(
      HTTPMethod.POST,
      `${this.RESIDENT_API_ENDPOINT}/residentCarePlan?applyBehaviorInterventionImport=true`,
      payload,
    );
  }

  public async generateBehaviorInterventionsCarePlanDocument(residentId: string, homeId: string) {
    const requestBody = {
      residentId,
      homeId,
    };
    return await this.invokeApi(
      HTTPMethod.POST,
      `${this.RESIDENT_API_ENDPOINT}/residentCarePlan?generateBehaviorInterventionsDocument=true`,
      requestBody,
    );
  }

  public async generatePreliminaryServiceCarePlanDocument(residentId: string, homeId: string) {
    const requestBody = {
      residentId,
      homeId,
    };
    return await this.invokeApi(
      HTTPMethod.POST,
      `${this.RESIDENT_API_ENDPOINT}/residentCarePlan?generatePreliminaryServicePlanDocument=true`,
      requestBody,
    );
  }

  public async generateConfiguredResidentCarePlanDocument(
    residentId: string,
    homeId: string,
    documentType: string,
    options?: {
      sourceFileName?: string;
      fileContentBase64?: string;
    },
  ) {
    return await this.invokeApi(
      HTTPMethod.POST,
      `${this.RESIDENT_API_ENDPOINT}/residentCarePlan?generateConfiguredDocument=true`,
      {
        residentId,
        homeId,
        documentType,
        ...(options?.sourceFileName ? { sourceFileName: options.sourceFileName } : {}),
        ...(options?.fileContentBase64 ? { fileContentBase64: options.fileContentBase64 } : {}),
      },
    );
  }

  public async generateResidentFacilityDocumentBatch(residentId: string, homeId: string) {
    return await this.invokeApi(
      HTTPMethod.POST,
      `${this.RESIDENT_API_ENDPOINT}/residentCarePlan?generateFacilityDocumentBatch=true`,
      {
        residentId,
        homeId,
      },
    );
  }

  public async getResidentFacilityDocumentDraft(residentId: string, documentType: string) {
    return await this.invokeApi(
      HTTPMethod.GET,
      `${this.RESIDENT_API_ENDPOINT}/residentCarePlan?residentId=${residentId}&facilityDocumentDraft=true&documentType=${documentType}`,
    );
  }

  public async saveResidentFacilityDocumentDraft(residentId: string, homeId: string, documentType: string, sections: any[]) {
    return await this.invokeApi(
      HTTPMethod.PUT,
      `${this.RESIDENT_API_ENDPOINT}/residentCarePlan?facilityDocumentDraft=true`,
      {
        residentId,
        homeId,
        documentType,
        sections,
      },
    );
  }

  public async generateResidentMarSheetDocument(residentId: string, homeId: string, formId?: string, medicationSectionData?: any) {
    const requestBody: any = {
      residentId,
      homeId,
    };
    if (formId) {
      requestBody.formId = formId;
    }
    if (medicationSectionData) {
      requestBody.medicationSectionData = medicationSectionData;
    }
    return await this.invokeApi(
      HTTPMethod.POST,
      `${this.RESIDENT_API_ENDPOINT}/residentCarePlan?generateMarSheetDocument=true`,
      requestBody,
    );
  }

  public async generateResidentProfileReportDocument(residentId: string, homeId: string) {
    return await this.invokeApi(
      HTTPMethod.POST,
      `${this.RESIDENT_API_ENDPOINT}/residentCarePlan?generateResidentProfileReportDocument=true`,
      {
        residentId,
        homeId,
      },
    );
  }

  public async generateEmergencyPacketDocument(payload: {
    residentId: string;
    homeId: string;
    packetRangeType: "last7Days" | "wholeMonth";
    selectedSections: string[];
  }) {
    return await this.invokeApi(
      HTTPMethod.POST,
      `${this.RESIDENT_API_ENDPOINT}/residentCarePlan?generateEmergencyPacketDocument=true`,
      payload,
    );
  }

  public async generateResidentEmarMarReportDocument(payload: {
    residentId: string;
    homeId: string;
    chartStartDate: string;
    chartEndDate: string;
    includeDailyTracking?: boolean;
    blankReport?: boolean;
  }) {
    return await this.invokeApi(
      HTTPMethod.POST,
      `${this.RESIDENT_API_ENDPOINT}/residentCarePlan?generateEmarMarReportDocument=true`,
      payload,
    );
  }

  public async generateAssessmentSummary(residentId: string, useExistingCredit: boolean = false) {
    return await this.invokeApi(
      HTTPMethod.GET,
      `${this.RESIDENT_API_ENDPOINT}/assessment?generate=true&residentId=${residentId}&useExistingCredit=${useExistingCredit}`,
    );
  }

  public async compareAssessmentSummary(residentId: string, useExistingCredit: boolean = false) {
    return await this.invokeApi(
      HTTPMethod.GET,
      `${this.RESIDENT_API_ENDPOINT}/assessment?compare=true&residentId=${residentId}&useExistingCredit=${useExistingCredit}`,
    );
  }

  public async createNurseAssessmentJob(requestBody: any) {
    return await this.invokeApi(
      HTTPMethod.POST,
      `${this.DOC_PARSER_API_ENDPOINT}/nurseAssessment`,
      requestBody,
    );
  }

  public async createProviderResidentAssessmentJob(requestBody: any) {
    return await this.invokeApi(
      HTTPMethod.POST,
      `${this.DOC_PARSER_API_ENDPOINT}/providerResidentAssessment`,
      requestBody,
    );
  }

  public async createStandaloneAssessmentSummaryJob(requestBody: any) {
    return await this.invokeApi(
      HTTPMethod.POST,
      `${this.DOC_PARSER_API_ENDPOINT}/assessmentSummary`,
      requestBody,
    );
  }

  public async getStandaloneAssessmentSummaryResult(jobId: string) {
    return await this.invokeApi(
      HTTPMethod.GET,
      `${this.DOC_PARSER_API_ENDPOINT}/assessmentSummary?jobId=${jobId}`,
    );
  }

  public async createStandaloneAssessmentCompareJob(requestBody: any) {
    return await this.invokeApi(
      HTTPMethod.POST,
      `${this.DOC_PARSER_API_ENDPOINT}/assessmentCompare`,
      requestBody,
    );
  }

  public async getStandaloneAssessmentCompareResult(jobId: string) {
    return await this.invokeApi(
      HTTPMethod.GET,
      `${this.DOC_PARSER_API_ENDPOINT}/assessmentCompare?jobId=${jobId}`,
    );
  }

  public async createStandaloneCarePlanCompareJob(requestBody: any) {
    return await this.invokeApi(
      HTTPMethod.POST,
      `${this.DOC_PARSER_API_ENDPOINT}/compareCarePlan`,
      requestBody,
    );
  }

  public async getStandaloneCarePlanCompareResult(jobId: string) {
    return await this.invokeApi(
      HTTPMethod.GET,
      `${this.DOC_PARSER_API_ENDPOINT}/compareCarePlan?jobId=${jobId}`,
    );
  }

  public async formatAiText(requestBody: {
    text?: string;
    taskType: string;
    tone: string;
    domain: string;
    outputStyle: string;
    metadata?: Record<string, any>;
  }) {
    return await this.invokeApi(
      HTTPMethod.POST,
      `${this.DOC_PARSER_API_ENDPOINT}/aiTextFormat`,
      requestBody,
    );
  }

  public async deleteNurseDelegationTask(residentId: string, taskId: string) {
    return await this.invokeApi(
      HTTPMethod.DELETE,
      `${this.NURSE_API_ENDPOINT}/nurseDelegationForms?residentId=${encodeURIComponent(residentId)}&tasks=true&taskId=${encodeURIComponent(taskId)}`,
    );
  }

  public async deleteProviderDelegationTask(residentId: string, taskId: string) {
    return await this.invokeApi(
      HTTPMethod.DELETE,
      `${this.RESIDENT_API_ENDPOINT}/residentDelegation?residentId=${encodeURIComponent(residentId)}&tasks=true&taskId=${encodeURIComponent(taskId)}`,
    );
  }

  public async retrieveAssessmentSummary(residentId: string) {
    return await this.invokeApi(
      HTTPMethod.GET,
      `${this.RESIDENT_API_ENDPOINT}/assessment?result=true&residentId=${residentId}`,
    );
  }

  public async retrieveCompareAssessmentSummary(residentId: string) {
    return await this.invokeApi(
      HTTPMethod.GET,
      `${this.RESIDENT_API_ENDPOINT}/assessment?compareResult=true&residentId=${residentId}`,
    );
  }

  public async getResidentCarePlanBundle(residentId: string) {
    return await this.invokeApi(
      HTTPMethod.GET,
      `${this.RESIDENT_API_ENDPOINT}/residentCarePlan?residentId=${residentId}`,
    );
  }

  public async getResidentAdlTracking(residentId: string, options?: { historyDateKey?: string }) {
    const params = new URLSearchParams({ residentId });
    if (options?.historyDateKey) {
      params.set("historyDateKey", options.historyDateKey);
    }
    return await this.invokeApi(
      HTTPMethod.GET,
      `${this.RESIDENT_API_ENDPOINT}/residentCarePlan?${params.toString()}&adlTracking=true`,
    );
  }

  public async saveResidentAdlDraft(requestBody: {
    residentId: string;
    homeId: string;
    draftTemplate: any;
  }) {
    return await this.invokeApi(
      HTTPMethod.PUT,
      `${this.RESIDENT_API_ENDPOINT}/residentCarePlan?adlTracking=true`,
      requestBody,
    );
  }

  public async publishResidentAdlDraft(requestBody: {
    residentId: string;
    homeId: string;
  }) {
    return await this.invokeApi(
      HTTPMethod.POST,
      `${this.RESIDENT_API_ENDPOINT}/residentCarePlan?adlTracking=true&publish=true`,
      requestBody,
    );
  }

  public async resetResidentAdlDraft(requestBody: {
    residentId: string;
    homeId: string;
  }) {
    return await this.invokeApi(
      HTTPMethod.POST,
      `${this.RESIDENT_API_ENDPOINT}/residentCarePlan?adlTracking=true&resetDraft=true`,
      requestBody,
    );
  }

  public async generateResidentAdlTasksFromCarePlan(requestBody: {
    residentId: string;
    homeId: string;
  }) {
    return await this.invokeApi(
      HTTPMethod.POST,
      `${this.RESIDENT_API_ENDPOINT}/residentCarePlan?adlTracking=true&generateFromCarePlan=true`,
      requestBody,
    );
  }

  public async saveResidentAdlEntry(requestBody: {
    residentId: string;
    homeId: string;
    dateKey: string;
    taskEntries: any[];
    correctionMode?: boolean;
    targetOccurrenceKey?: string;
    expectedVersion?: number;
  }) {
    return await this.invokeApi(
      HTTPMethod.POST,
      `${this.RESIDENT_API_ENDPOINT}/residentCarePlan?adlTracking=true&saveEntry=true`,
      requestBody,
    );
  }

  public async generateResidentAdlReportDocument(requestBody: {
    residentId: string;
    homeId: string;
    reportMonth?: string;
    reportRange?: "monthly" | "last7Days" | "custom";
    reportStartDate?: string;
    reportEndDate?: string;
    blankReport?: boolean;
  }) {
    return await this.invokeApi(
      HTTPMethod.POST,
      `${this.RESIDENT_API_ENDPOINT}/residentCarePlan?adlTracking=true&generateReportDocument=true`,
      requestBody,
    );
  }

  public async getResidentCarePlan(residentId: string) {
    const response = await this.getResidentCarePlanBundle(residentId);
    if (Array.isArray(response)) {
      return response;
    }
    return Array.isArray(response?.items) ? response.items : [];
  }

  public async getResidentBehaviorSnapshot(residentId: string) {
    return await this.invokeApi(
      HTTPMethod.GET,
      `${this.RESIDENT_API_ENDPOINT}/residentBehavior?residentId=${residentId}`,
    );
  }

  public async getResidentSchedules(residentId: string) {
    return await this.invokeApi(
      HTTPMethod.GET,
      `${this.RESIDENT_API_ENDPOINT}/residentSchedule?residentId=${encodeURIComponent(residentId)}`,
    );
  }

  public async getHomeResidentScheduleSummaries(homeId: string) {
    return await this.invokeApi(
      HTTPMethod.GET,
      `${this.RESIDENT_API_ENDPOINT}/residentSchedule?homeId=${encodeURIComponent(homeId)}`,
    );
  }

  public async createResidentSchedule(requestBody: Record<string, any>) {
    return await this.invokeApi(HTTPMethod.POST, `${this.RESIDENT_API_ENDPOINT}/residentSchedule`, requestBody);
  }

  public async updateResidentSchedule(requestBody: Record<string, any>) {
    return await this.invokeApi(HTTPMethod.PUT, `${this.RESIDENT_API_ENDPOINT}/residentSchedule`, requestBody);
  }

  public async completeResidentSchedule(requestBody: Record<string, any>) {
    return await this.invokeApi(HTTPMethod.POST, `${this.RESIDENT_API_ENDPOINT}/residentSchedule`, { ...requestBody, action: "complete" });
  }

  public async skipResidentSchedule(requestBody: Record<string, any>) {
    return await this.invokeApi(HTTPMethod.POST, `${this.RESIDENT_API_ENDPOINT}/residentSchedule`, { ...requestBody, action: "skip" });
  }

  public async endResidentSchedule(residentId: string, scheduleId: string) {
    return await this.invokeApi(
      HTTPMethod.DELETE,
      `${this.RESIDENT_API_ENDPOINT}/residentSchedule?residentId=${encodeURIComponent(residentId)}&scheduleId=${encodeURIComponent(scheduleId)}`,
    );
  }

  public async getFacilityCareActivity(homeId: string, options?: {
    residentId?: string;
    activityType?: string;
    dateKey?: string;
    limit?: number;
    recentHours?: number;
    nextToken?: string;
    previousToken?: string;
  }) {
    const params = new URLSearchParams({ homeId });
    if (options?.residentId) params.set("residentId", options.residentId);
    if (options?.activityType) params.set("activityType", options.activityType);
    if (options?.dateKey) params.set("dateKey", options.dateKey);
    if (options?.limit) params.set("limit", String(options.limit));
    if (options?.recentHours) params.set("recentHours", String(options.recentHours));
    if (options?.nextToken) params.set("nextToken", options.nextToken);
    if (options?.previousToken) params.set("previousToken", options.previousToken);
    return await this.invokeApi(
      HTTPMethod.GET,
      `${this.RESIDENT_API_ENDPOINT}/facilityCareActivity?${params.toString()}`,
    );
  }

  public async resetSharedContent(confirmation = "RESET") {
    return await this.invokeApi(HTTPMethod.POST, `${this.BINDER_API_ENDPOINT}/binderShare?resetSharedContent=true`, { confirmation });
  }

  public async startFacilityTaskImportJob(requestBody: { homeId: string; objectKey: string; fileName: string }) {
    return await this.addJobDetails({
      ...requestBody,
      jobType: "FACILITY_TASK_IMPORT",
      referenceId: requestBody.homeId,
    });
  }

  public async getFacilityTaskImportJob(jobId: string) {
    return await this.getJobDetails(jobId);
  }

  public async getFacilityTasks(homeId: string, options?: { dateKey?: string; assignedToMe?: boolean; staffPass?: boolean; view?: "upcoming" | "previous"; taskState?: "active" | "deleted"; cursor?: string | null; pageSize?: number; sortField?: string; sortOrder?: "asc" | "desc" }) {
    const params = new URLSearchParams({ homeId });
    if (options?.dateKey) params.set("dateKey", options.dateKey);
    if (options?.assignedToMe) params.set("assignedToMe", "true");
    if (options?.staffPass) params.set("staffPass", "true");
    if (options?.view) params.set("view", options.view);
    if (options?.taskState) params.set("taskState", options.taskState);
    if (options?.cursor) params.set("cursor", options.cursor);
    if (options?.pageSize) params.set("pageSize", String(options.pageSize));
    if (options?.sortField) params.set("sortField", options.sortField);
    if (options?.sortOrder) params.set("sortOrder", options.sortOrder);
    return await this.invokeApi(HTTPMethod.GET, `${this.HOME_API_ENDPOINT}/facilityTasks?${params.toString()}`);
  }

  public async getFacilityTaskTemplates(homeId: string) {
    return await this.invokeApi(
      HTTPMethod.GET,
      `${this.HOME_API_ENDPOINT}/facilityTasks?homeId=${encodeURIComponent(homeId)}&templates=true`,
    );
  }

  public async getFacilityTaskPastEntries(homeId: string, dateKey: string) {
    const params = new URLSearchParams({ homeId, dateKey, pastEntries: "true" });
    return await this.invokeApi(HTTPMethod.GET, `${this.HOME_API_ENDPOINT}/facilityTasks?${params.toString()}`);
  }

  public async getFacilityTaskEntryHistory(homeId: string, taskId: string, dateKey: string) {
    const params = new URLSearchParams({ homeId, taskId, dateKey, history: "true" });
    return await this.invokeApi(HTTPMethod.GET, `${this.HOME_API_ENDPOINT}/facilityTasks?${params.toString()}`);
  }

  public async createFacilityTask(requestBody: Record<string, any>) {
    return await this.invokeApi(HTTPMethod.POST, `${this.HOME_API_ENDPOINT}/facilityTasks`, requestBody);
  }

  public async resetFacilityTasks(homeId: string, confirmation = "RESET") {
    return await this.invokeApi(HTTPMethod.POST, `${this.HOME_API_ENDPOINT}/facilityTasks`, { homeId, action: "reset", confirmation });
  }

  public async exportFacilityTaskWeeklyActivity(requestBody: { homeId: string; staffId: string; residentId?: string; weekStart: string; view?: "list" | "grid" }) {
    return await this.invokeApi(HTTPMethod.POST, `${this.HOME_API_ENDPOINT}/facilityTasks`, { ...requestBody, action: "weeklyExport" });
  }

  public async createFacilityTaskCategory(homeId: string, label: string) {
    return await this.invokeApi(HTTPMethod.POST, `${this.HOME_API_ENDPOINT}/facilityTasks`, { homeId, action: "createCategory", label });
  }

  public async updateFacilityTaskCategory(homeId: string, categoryId: string, label: string) {
    return await this.invokeApi(HTTPMethod.POST, `${this.HOME_API_ENDPOINT}/facilityTasks`, { homeId, action: "updateCategory", categoryId, label });
  }

  public async archiveFacilityTaskCategory(homeId: string, categoryId: string) {
    return await this.invokeApi(HTTPMethod.POST, `${this.HOME_API_ENDPOINT}/facilityTasks`, { homeId, action: "archiveCategory", categoryId });
  }

  public async updateFacilityTask(requestBody: Record<string, any>) {
    return await this.invokeApi(HTTPMethod.PUT, `${this.HOME_API_ENDPOINT}/facilityTasks`, requestBody);
  }

  public async bulkAssignFacilityTasks(requestBody: { homeId: string; taskIds: string[]; assignedStaffIds: string[] }) {
    return await this.invokeApi(HTTPMethod.PUT, `${this.HOME_API_ENDPOINT}/facilityTasks`, { ...requestBody, action: "bulkAssignee" });
  }

  public async updateFacilityTaskStatus(requestBody: Record<string, any>) {
    return await this.invokeApi(HTTPMethod.PUT, `${this.HOME_API_ENDPOINT}/facilityTasks`, { ...requestBody, action: "status" });
  }

  public async correctFacilityTaskEntry(requestBody: { homeId: string; taskId: string; dateKey: string; status: string; note: string }) {
    return await this.invokeApi(HTTPMethod.PUT, `${this.HOME_API_ENDPOINT}/facilityTasks`, { ...requestBody, action: "correction" });
  }

  public async archiveFacilityTask(homeId: string, taskId: string) {
    return await this.invokeApi(HTTPMethod.DELETE, `${this.HOME_API_ENDPOINT}/facilityTasks?homeId=${encodeURIComponent(homeId)}&taskId=${encodeURIComponent(taskId)}`);
  }

  public async getResidentBehaviorTimeline(residentId: string, limit: number = 50) {
    return await this.invokeApi(
      HTTPMethod.GET,
      `${this.RESIDENT_API_ENDPOINT}/residentBehavior?residentId=${residentId}&timeline=true&limit=${limit}`,
    );
  }

  public async importResidentBehaviorTracking(residentId: string, importMode: "auto" | "manual" = "manual") {
    return await this.invokeApi(
      HTTPMethod.POST,
      `${this.RESIDENT_API_ENDPOINT}/residentBehavior?residentId=${residentId}&importMode=${importMode}`,
    );
  }

  public async getResidentBehaviorLogEntries(residentId: string, limit: number = 200) {
    return await this.invokeApi(
      HTTPMethod.GET,
      `${this.RESIDENT_API_ENDPOINT}/residentBehaviorLog?residentId=${residentId}&limit=${limit}`,
    );
  }

  public async getResidentBehaviorLogEntriesPage(
    residentId: string,
    options?: { limit?: number; nextToken?: string }
  ) {
    const params = new URLSearchParams({
      residentId,
      paged: "true",
      limit: String(options?.limit || 10),
    });
    if (options?.nextToken) {
      params.set("nextToken", options.nextToken);
    }
    return await this.invokeApi(
      HTTPMethod.GET,
      `${this.RESIDENT_API_ENDPOINT}/residentBehaviorLog?${params.toString()}`,
    );
  }

  public async getResidentBehaviorLogCbhsMonthlyDataset(
    residentId: string,
    options: { reportRange: "today" | "last7Days" | "monthly"; reportMonth?: string; behaviorHoursMode?: "common" | "separate" },
  ) {
    const params = new URLSearchParams({
      residentId,
      reportRange: options.reportRange,
    });
    if (options.reportRange === "monthly" && options.reportMonth) {
      params.set("cbhsMonth", options.reportMonth);
    }
    params.set("behaviorHoursMode", options.behaviorHoursMode === "separate" ? "separate" : "common");
    return await this.invokeApi(
      HTTPMethod.GET,
      `${this.RESIDENT_API_ENDPOINT}/residentBehaviorLog?${params.toString()}`,
    );
  }

  public async generateResidentBehaviorLogReportDocument(residentId: string, requestBody: {
    dataset: any;
    providerOneId?: string;
    residentDob?: string;
    printedName?: string;
    signatureDate?: string;
    includeTierDetails?: boolean;
  }) {
    return await this.invokeApi(
      HTTPMethod.POST,
      `${this.RESIDENT_API_ENDPOINT}/residentBehaviorLog?residentId=${residentId}&generateReportDocument=true`,
      requestBody,
    );
  }

  public async createResidentBehaviorLogEntry(residentId: string, requestBody: Record<string, any>) {
    return await this.invokeApi(
      HTTPMethod.POST,
      `${this.RESIDENT_API_ENDPOINT}/residentBehaviorLog?residentId=${residentId}`,
      requestBody,
    );
  }

  public async previewResidentBehaviorLogImport(residentId: string, requestBody: {
    fileName: string;
    mimeType?: string;
    fileContentBase64: string;
  }) {
    return await this.invokeApi(
      HTTPMethod.POST,
      `${this.RESIDENT_API_ENDPOINT}/residentBehaviorLog?residentId=${residentId}`,
      {
        importAction: "preview",
        ...requestBody,
      },
    );
  }

  public async previewResidentDailyLogImport(residentId: string, requestBody: {
    fileName: string;
    mimeType?: string;
    fileContentBase64: string;
  }) {
    return await this.invokeApi(
      HTTPMethod.POST,
      `${this.RESIDENT_API_ENDPOINT}/residentDailyLogImport?residentId=${residentId}`,
      requestBody,
    );
  }

  public async getResidentDailyLogImportPreviewJob(residentId: string, jobId: string) {
    return await this.invokeApi(
      HTTPMethod.GET,
      `${this.RESIDENT_API_ENDPOINT}/residentDailyLogImport?residentId=${residentId}&jobId=${encodeURIComponent(jobId)}`,
    );
  }

  public async getResidentDailyLogImportLastSummary(residentId: string) {
    return await this.invokeApi(
      HTTPMethod.GET,
      `${this.RESIDENT_API_ENDPOINT}/residentDailyLogImport?residentId=${residentId}&summary=true`,
    );
  }

  public async updateResidentDailyLogImportLastSummary(residentId: string, summary: Record<string, any>) {
    return await this.invokeApi(
      HTTPMethod.PUT,
      `${this.RESIDENT_API_ENDPOINT}/residentDailyLogImport?residentId=${residentId}`,
      { summary },
    );
  }

  public async updateResidentBehaviorLogEntry(residentId: string, requestBody: Record<string, any>) {
    return await this.invokeApi(
      HTTPMethod.PUT,
      `${this.RESIDENT_API_ENDPOINT}/residentBehaviorLog?residentId=${residentId}`,
      requestBody,
    );
  }

  public async deleteResidentBehaviorLogEntry(residentId: string, behaviorLogEntryId: string) {
    return await this.invokeApi(
      HTTPMethod.DELETE,
      `${this.RESIDENT_API_ENDPOINT}/residentBehaviorLog?residentId=${residentId}&behaviorLogEntryId=${encodeURIComponent(behaviorLogEntryId)}`,
    );
  }

  public async getResidentIncidentReports(residentId: string, limit: number = 200) {
    return await this.invokeApi(
      HTTPMethod.GET,
      `${this.RESIDENT_API_ENDPOINT}/residentIncidentReport?residentId=${residentId}&limit=${limit}`,
    );
  }

  public async getResidentIncidentReportsPage(
    residentId: string,
    options?: { limit?: number; nextToken?: string }
  ) {
    const params = new URLSearchParams({
      residentId,
      paged: "true",
      limit: String(options?.limit || 10),
    });
    if (options?.nextToken) {
      params.set("nextToken", options.nextToken);
    }
    return await this.invokeApi(
      HTTPMethod.GET,
      `${this.RESIDENT_API_ENDPOINT}/residentIncidentReport?${params.toString()}`,
    );
  }

  public async createResidentIncidentReport(residentId: string, requestBody: Record<string, any>) {
    return await this.invokeApi(
      HTTPMethod.POST,
      `${this.RESIDENT_API_ENDPOINT}/residentIncidentReport?residentId=${residentId}`,
      requestBody,
    );
  }

  public async updateResidentIncidentReport(residentId: string, requestBody: Record<string, any>) {
    return await this.invokeApi(
      HTTPMethod.PUT,
      `${this.RESIDENT_API_ENDPOINT}/residentIncidentReport?residentId=${residentId}`,
      requestBody,
    );
  }

  public async deleteResidentIncidentReport(residentId: string, entryLogId: string) {
    return await this.invokeApi(
      HTTPMethod.DELETE,
      `${this.RESIDENT_API_ENDPOINT}/residentIncidentReport?residentId=${residentId}&entryLogId=${encodeURIComponent(entryLogId)}`,
    );
  }

  public async getResidentIncidentReportMonthlyDataset(
    residentId: string,
    options: { reportRange: "today" | "last7Days" | "monthly"; reportMonth?: string },
  ) {
    const params = new URLSearchParams({
      residentId,
      reportRange: options.reportRange,
    });
    if (options.reportRange === "monthly" && options.reportMonth) {
      params.set("reportMonth", options.reportMonth);
    }
    return await this.invokeApi(
      HTTPMethod.GET,
      `${this.RESIDENT_API_ENDPOINT}/residentIncidentReport?${params.toString()}`,
    );
  }

  public async generateResidentIncidentReportDocument(residentId: string, requestBody: {
    dataset: any;
  }) {
    return await this.invokeApi(
      HTTPMethod.POST,
      `${this.RESIDENT_API_ENDPOINT}/residentIncidentReport?residentId=${residentId}&generateReportDocument=true`,
      requestBody,
    );
  }

  public async getResidentDailyNotes(residentId: string, limit: number = 200) {
    return await this.invokeApi(
      HTTPMethod.GET,
      `${this.RESIDENT_API_ENDPOINT}/residentDailyNote?residentId=${residentId}&limit=${limit}`,
    );
  }

  public async getResidentDailyNotesPage(
    residentId: string,
    options?: { limit?: number; nextToken?: string }
  ) {
    const params = new URLSearchParams({
      residentId,
      paged: "true",
      limit: String(options?.limit || 10),
    });
    if (options?.nextToken) {
      params.set("nextToken", options.nextToken);
    }
    return await this.invokeApi(
      HTTPMethod.GET,
      `${this.RESIDENT_API_ENDPOINT}/residentDailyNote?${params.toString()}`,
    );
  }

  public async createResidentDailyNote(residentId: string, requestBody: Record<string, any>) {
    return await this.invokeApi(
      HTTPMethod.POST,
      `${this.RESIDENT_API_ENDPOINT}/residentDailyNote?residentId=${residentId}`,
      requestBody,
    );
  }

  public async createResidentMovementEntry(residentId: string, requestBody: Record<string, any>) {
    return await this.invokeApi(
      HTTPMethod.POST,
      `${this.RESIDENT_API_ENDPOINT}/residentMovement?residentId=${residentId}`,
      requestBody,
    );
  }

  public async getResidentMovementEntriesPage(residentId: string, options?: { limit?: number; nextToken?: string }) {
    const params = new URLSearchParams({ residentId, paged: "true", limit: String(options?.limit || 10) });
    if (options?.nextToken) params.set("nextToken", options.nextToken);
    return await this.invokeApi(HTTPMethod.GET, `${this.RESIDENT_API_ENDPOINT}/residentMovement?${params.toString()}`);
  }

  public async updateResidentMovementEntry(residentId: string, requestBody: Record<string, any>) {
    return await this.invokeApi(HTTPMethod.PUT, `${this.RESIDENT_API_ENDPOINT}/residentMovement?residentId=${residentId}`, requestBody);
  }

  public async deleteResidentMovementEntry(residentId: string, entryLogId: string) {
    return await this.invokeApi(HTTPMethod.DELETE, `${this.RESIDENT_API_ENDPOINT}/residentMovement?residentId=${residentId}&entryLogId=${encodeURIComponent(entryLogId)}`);
  }

  public async getResidentMovementMonthlyDataset(residentId: string, options: { reportRange: "today" | "last7Days" | "monthly"; reportMonth?: string }) {
    const params = new URLSearchParams({ residentId, reportRange: options.reportRange });
    if (options.reportRange === "monthly" && options.reportMonth) params.set("reportMonth", options.reportMonth);
    return await this.invokeApi(HTTPMethod.GET, `${this.RESIDENT_API_ENDPOINT}/residentMovement?${params.toString()}`);
  }

  public async generateResidentMovementReportDocument(residentId: string, requestBody: { dataset: any }) {
    return await this.invokeApi(HTTPMethod.POST, `${this.RESIDENT_API_ENDPOINT}/residentMovement?residentId=${residentId}&generateReportDocument=true`, requestBody);
  }

  public async updateResidentDailyNote(residentId: string, requestBody: Record<string, any>) {
    return await this.invokeApi(
      HTTPMethod.PUT,
      `${this.RESIDENT_API_ENDPOINT}/residentDailyNote?residentId=${residentId}`,
      requestBody,
    );
  }

  public async deleteResidentDailyNote(residentId: string, entryLogId: string) {
    return await this.invokeApi(
      HTTPMethod.DELETE,
      `${this.RESIDENT_API_ENDPOINT}/residentDailyNote?residentId=${residentId}&entryLogId=${encodeURIComponent(entryLogId)}`,
    );
  }

  public async getResidentDailyNotesMonthlyDataset(
    residentId: string,
    options: { reportRange: "today" | "last7Days" | "monthly"; reportMonth?: string },
  ) {
    const params = new URLSearchParams({
      residentId,
      reportRange: options.reportRange,
    });
    if (options.reportRange === "monthly" && options.reportMonth) {
      params.set("reportMonth", options.reportMonth);
    }
    return await this.invokeApi(
      HTTPMethod.GET,
      `${this.RESIDENT_API_ENDPOINT}/residentDailyNote?${params.toString()}`,
    );
  }

  public async generateResidentDailyNotesReportDocument(residentId: string, requestBody: {
    dataset: any;
  }) {
    return await this.invokeApi(
      HTTPMethod.POST,
      `${this.RESIDENT_API_ENDPOINT}/residentDailyNote?residentId=${residentId}&generateReportDocument=true`,
      requestBody,
    );
  }

  public async getCaregiverResidentBehaviorLogEntries(residentId: string, limit: number = 200) {
    return await this.invokeApi(
      HTTPMethod.GET,
      `${this.CAREGIVER_API_ENDPOINT}/caregiverResidentBehaviorLog?residentId=${residentId}&limit=${limit}`,
    );
  }

  public async getCaregiverResidentBehaviorLogEntriesPage(
    residentId: string,
    options?: { limit?: number; nextToken?: string }
  ) {
    const params = new URLSearchParams({
      residentId,
      paged: "true",
      limit: String(options?.limit || 10),
    });
    if (options?.nextToken) {
      params.set("nextToken", options.nextToken);
    }
    return await this.invokeApi(
      HTTPMethod.GET,
      `${this.CAREGIVER_API_ENDPOINT}/caregiverResidentBehaviorLog?${params.toString()}`,
    );
  }

  public async createCaregiverResidentBehaviorLogEntry(residentId: string, requestBody: Record<string, any>) {
    return await this.invokeApi(
      HTTPMethod.POST,
      `${this.CAREGIVER_API_ENDPOINT}/caregiverResidentBehaviorLog?residentId=${residentId}`,
      requestBody,
    );
  }

  public async updateCaregiverResidentBehaviorLogEntry(residentId: string, requestBody: Record<string, any>) {
    return await this.invokeApi(
      HTTPMethod.PUT,
      `${this.CAREGIVER_API_ENDPOINT}/caregiverResidentBehaviorLog?residentId=${residentId}`,
      requestBody,
    );
  }

  public async deleteCaregiverResidentBehaviorLogEntry(residentId: string, behaviorLogEntryId: string) {
    return await this.invokeApi(
      HTTPMethod.DELETE,
      `${this.CAREGIVER_API_ENDPOINT}/caregiverResidentBehaviorLog?residentId=${residentId}&behaviorLogEntryId=${encodeURIComponent(behaviorLogEntryId)}`,
    );
  }

  public async getCaregiverResidentIncidentReports(residentId: string, limit: number = 200) {
    return await this.invokeApi(
      HTTPMethod.GET,
      `${this.CAREGIVER_API_ENDPOINT}/caregiverResidentIncidentReport?residentId=${residentId}&limit=${limit}`,
    );
  }

  public async getCaregiverResidentIncidentReportsPage(
    residentId: string,
    options?: { limit?: number; nextToken?: string }
  ) {
    const params = new URLSearchParams({
      residentId,
      paged: "true",
      limit: String(options?.limit || 10),
    });
    if (options?.nextToken) {
      params.set("nextToken", options.nextToken);
    }
    return await this.invokeApi(
      HTTPMethod.GET,
      `${this.CAREGIVER_API_ENDPOINT}/caregiverResidentIncidentReport?${params.toString()}`,
    );
  }

  public async createCaregiverResidentIncidentReport(residentId: string, requestBody: Record<string, any>) {
    return await this.invokeApi(
      HTTPMethod.POST,
      `${this.CAREGIVER_API_ENDPOINT}/caregiverResidentIncidentReport?residentId=${residentId}`,
      requestBody,
    );
  }

  public async updateCaregiverResidentIncidentReport(residentId: string, requestBody: Record<string, any>) {
    return await this.invokeApi(
      HTTPMethod.PUT,
      `${this.CAREGIVER_API_ENDPOINT}/caregiverResidentIncidentReport?residentId=${residentId}`,
      requestBody,
    );
  }

  public async deleteCaregiverResidentIncidentReport(residentId: string, entryLogId: string) {
    return await this.invokeApi(
      HTTPMethod.DELETE,
      `${this.CAREGIVER_API_ENDPOINT}/caregiverResidentIncidentReport?residentId=${residentId}&entryLogId=${encodeURIComponent(entryLogId)}`,
    );
  }

  public async getCaregiverResidentDailyNotes(residentId: string, limit: number = 200) {
    return await this.invokeApi(
      HTTPMethod.GET,
      `${this.CAREGIVER_API_ENDPOINT}/caregiverResidentDailyNote?residentId=${residentId}&limit=${limit}`,
    );
  }

  public async getCaregiverResidentDailyNotesPage(
    residentId: string,
    options?: { limit?: number; nextToken?: string }
  ) {
    const params = new URLSearchParams({
      residentId,
      paged: "true",
      limit: String(options?.limit || 10),
    });
    if (options?.nextToken) {
      params.set("nextToken", options.nextToken);
    }
    return await this.invokeApi(
      HTTPMethod.GET,
      `${this.CAREGIVER_API_ENDPOINT}/caregiverResidentDailyNote?${params.toString()}`,
    );
  }

  public async createCaregiverResidentDailyNote(residentId: string, requestBody: Record<string, any>) {
    return await this.invokeApi(
      HTTPMethod.POST,
      `${this.CAREGIVER_API_ENDPOINT}/caregiverResidentDailyNote?residentId=${residentId}`,
      requestBody,
    );
  }

  public async createCaregiverResidentMovementEntry(residentId: string, requestBody: Record<string, any>) {
    return await this.invokeApi(
      HTTPMethod.POST,
      `${this.CAREGIVER_API_ENDPOINT}/caregiverResidentMovement?residentId=${residentId}`,
      requestBody,
    );
  }

  public async getCaregiverResidentMovementEntriesPage(residentId: string, options?: { limit?: number; nextToken?: string }) {
    const params = new URLSearchParams({ residentId, paged: "true", limit: String(options?.limit || 10) });
    if (options?.nextToken) params.set("nextToken", options.nextToken);
    return await this.invokeApi(HTTPMethod.GET, `${this.CAREGIVER_API_ENDPOINT}/caregiverResidentMovement?${params.toString()}`);
  }

  public async updateCaregiverResidentMovementEntry(residentId: string, requestBody: Record<string, any>) {
    return await this.invokeApi(HTTPMethod.PUT, `${this.CAREGIVER_API_ENDPOINT}/caregiverResidentMovement?residentId=${residentId}`, requestBody);
  }

  public async deleteCaregiverResidentMovementEntry(residentId: string, entryLogId: string) {
    return await this.invokeApi(HTTPMethod.DELETE, `${this.CAREGIVER_API_ENDPOINT}/caregiverResidentMovement?residentId=${residentId}&entryLogId=${encodeURIComponent(entryLogId)}`);
  }

  public async updateCaregiverResidentDailyNote(residentId: string, requestBody: Record<string, any>) {
    return await this.invokeApi(
      HTTPMethod.PUT,
      `${this.CAREGIVER_API_ENDPOINT}/caregiverResidentDailyNote?residentId=${residentId}`,
      requestBody,
    );
  }

  public async deleteCaregiverResidentDailyNote(residentId: string, entryLogId: string) {
    return await this.invokeApi(
      HTTPMethod.DELETE,
      `${this.CAREGIVER_API_ENDPOINT}/caregiverResidentDailyNote?residentId=${residentId}&entryLogId=${encodeURIComponent(entryLogId)}`,
    );
  }

  public async getResidentCarePlanTrackingSnapshot(residentId: string) {
    return await this.invokeApi(
      HTTPMethod.GET,
      `${this.RESIDENT_API_ENDPOINT}/residentCarePlanTracking?residentId=${residentId}`,
    );
  }

  public async getResidentCarePlanTrackingTimeline(residentId: string, limit: number = 50) {
    return await this.invokeApi(
      HTTPMethod.GET,
      `${this.RESIDENT_API_ENDPOINT}/residentCarePlanTracking?residentId=${residentId}&timeline=true&limit=${limit}`,
    );
  }

  public async importResidentCarePlanTracking(residentId: string, importMode: "auto" | "manual" = "manual") {
    return await this.invokeApi(
      HTTPMethod.POST,
      `${this.RESIDENT_API_ENDPOINT}/residentCarePlanTracking?residentId=${residentId}&importMode=${importMode}`,
    );
  }

  public async addWebsiteInquiry(inquiryData: WebsiteInquiry) {
    return await this.invokeApi(
      HTTPMethod.POST,
      `${this.HOME_WEBSITE_API_ENDPOINT}/inquiry`,
      inquiryData,
    );
  }

  public async dismissWebsiteInquiry(inquiryId: string, homeId: string) {
    return await this.invokeApi(
      HTTPMethod.DELETE,
      `${this.HOME_WEBSITE_API_ENDPOINT}/inquiry?inquiryId=${inquiryId}&homeId=${homeId}`,
    );
  }

  public async listWebsiteInquiries(homeId: string, lastEvaluatedKey: string) {
    return await this.invokeApi(
      HTTPMethod.GET,
      `${this.HOME_WEBSITE_API_ENDPOINT}/inquiry?homeId=${homeId}&lastEvaluatedKey=${lastEvaluatedKey}`,
    );
  }

  public async getWebsiteMetrics(homeId: string, duration: string) {
    return await this.invokeApi(
      HTTPMethod.GET,
      `${this.HOME_WEBSITE_API_ENDPOINT}/websiteMetrics?duration=${duration}&homeId=${homeId}`
    );
  }

  public async addWebsitePageMetrics(requestBody: any) {
    return await this.invokeApi(
      HTTPMethod.POST,
      `${this.HOME_WEBSITE_API_ENDPOINT}/websiteMetrics`,
      requestBody,
    );
  }

  public async sendNotification(requestBody: any) {
    return await this.invokeApi(
      HTTPMethod.POST,
      `${this.HOME_API_ENDPOINT}/notification`,
      requestBody,
    );
  }

  public async getSharedCalendarLookups(workspaceId?: string) {
    const params = new URLSearchParams();
    if (workspaceId) {
      params.set("workspaceId", workspaceId);
    }
    return await this.invokeApi(
      HTTPMethod.GET,
      `${this.SHARED_CALENDAR_API_ENDPOINT}/calendar/lookups${params.toString() ? `?${params.toString()}` : ""}`,
    );
  }

  public async getSharedCalendarEventTypes() {
    return await this.invokeApi(
      HTTPMethod.GET,
      `${this.SHARED_CALENDAR_API_ENDPOINT}/calendar/event-types`,
    );
  }

  public async createSharedCalendarEventType(requestBody: { label: string; color?: string }) {
    return await this.invokeApi(
      HTTPMethod.POST,
      `${this.SHARED_CALENDAR_API_ENDPOINT}/calendar/event-types`,
      requestBody,
    );
  }

  public async updateSharedCalendarEventType(requestBody: { eventTypeId: string; label: string; color?: string }) {
    return await this.invokeApi(
      HTTPMethod.PUT,
      `${this.SHARED_CALENDAR_API_ENDPOINT}/calendar/event-types`,
      requestBody,
    );
  }

  public async archiveSharedCalendarEventType(eventTypeId: string) {
    return await this.invokeApi(
      HTTPMethod.DELETE,
      `${this.SHARED_CALENDAR_API_ENDPOINT}/calendar/event-types?eventTypeId=${encodeURIComponent(eventTypeId)}`,
    );
  }

  public async getSharedCalendarEvents(options?: { workspaceId?: string; start?: string; end?: string; }) {
    const params = new URLSearchParams();
    if (options?.workspaceId) {
      params.set("workspaceId", options.workspaceId);
    }
    if (options?.start) {
      params.set("start", options.start);
    }
    if (options?.end) {
      params.set("end", options.end);
    }
    return await this.invokeApi(
      HTTPMethod.GET,
      `${this.SHARED_CALENDAR_API_ENDPOINT}/calendar${params.toString() ? `?${params.toString()}` : ""}`,
    );
  }

  public async createSharedCalendarEvent(requestBody: any) {
    return await this.invokeApi(
      HTTPMethod.POST,
      `${this.SHARED_CALENDAR_API_ENDPOINT}/calendar`,
      requestBody,
    );
  }

  public async updateSharedCalendarEvent(requestBody: any) {
    return await this.invokeApi(
      HTTPMethod.PUT,
      `${this.SHARED_CALENDAR_API_ENDPOINT}/calendar`,
      requestBody,
    );
  }

  public async cancelSharedCalendarEvent(eventId: string) {
    return await this.invokeApi(
      HTTPMethod.DELETE,
      `${this.SHARED_CALENDAR_API_ENDPOINT}/calendar?eventId=${encodeURIComponent(eventId)}`,
    );
  }

  public async deleteSharedCalendarEvent(eventId: string, mode = "delete") {
    return await this.invokeApi(
      HTTPMethod.DELETE,
      `${this.SHARED_CALENDAR_API_ENDPOINT}/calendar?eventId=${encodeURIComponent(eventId)}&mode=${encodeURIComponent(mode)}`,
    );
  }

  public async resetSharedCalendar(confirmation = "RESET") {
    return await this.invokeApi(HTTPMethod.POST, `${this.SHARED_CALENDAR_API_ENDPOINT}/calendar`, { action: "reset", confirmation });
  }

  public async getSharedCalendarHistory(eventId: string, workspaceId?: string) {
    const params = new URLSearchParams({
      eventId,
    });
    if (workspaceId) {
      params.set("workspaceId", workspaceId);
    }
    return await this.invokeApi(
      HTTPMethod.GET,
      `${this.SHARED_CALENDAR_API_ENDPOINT}/calendar/history?${params.toString()}`,
    );
  }

  public async applySharedCalendarOccurrenceAction(requestBody: any) {
    return await this.invokeApi(
      HTTPMethod.POST,
      `${this.SHARED_CALENDAR_API_ENDPOINT}/calendar/occurrence-action`,
      requestBody,
    );
  }

  public async getSubscription(userId: string, roleType?: string) {
    const queryParams = new URLSearchParams({
      userId,
    });
    if (roleType) {
      queryParams.set("roleType", roleType);
    }
    return await this.invokeApi(
      HTTPMethod.GET,
      `${this.AUTH_API_ENDPOINT}/subscription?${queryParams.toString()}`,
    );
  }

  public async activateAccessUnlock() {
    return await this.invokeApi(
      HTTPMethod.POST,
      `${this.AUTH_API_ENDPOINT}/subscription?accessUnlock=true`,
      {},
    );
  }

  public async getPaymentHistory(userId: string, options?: { invoiceCursor?: string | null; featureCursor?: string | null; invoiceLimit?: number; featureLimit?: number; }) {
    const queryParams = new URLSearchParams({
      userId,
      paymentHistory: "true",
    });
    if (options?.invoiceCursor) {
      queryParams.set("invoiceCursor", options.invoiceCursor);
    }
    if (options?.featureCursor) {
      queryParams.set("featureCursor", options.featureCursor);
    }
    if (options?.invoiceLimit) {
      queryParams.set("invoiceLimit", options.invoiceLimit.toString());
    }
    if (options?.featureLimit) {
      queryParams.set("featureLimit", options.featureLimit.toString());
    }
    return await this.invokeApi(
      HTTPMethod.GET,
      `${this.AUTH_API_ENDPOINT}/subscription?${queryParams.toString()}`,
    );
  }

  public async getTaxDetails(totalPrice: number, billingAddress?: any) {
    if (billingAddress) {
      const response = await this.invokeApi(
        HTTPMethod.POST,
        `${this.AUTH_API_ENDPOINT}/subscription?calculateTax=true`,
        {
          totalPrice,
          billingAddress,
        },
      );

      return response?.taxDetails !== undefined
        ? { ...(response?.taxDetails || {}), billingAddress: response?.billingAddress || null, requiresBillingAddress: !!response?.requiresBillingAddress }
        : response;
    }

    const response = await this.invokeApi(
      HTTPMethod.GET,
      `${this.AUTH_API_ENDPOINT}/subscription?calculateTax=true&totalPrice=${totalPrice}`,
    );

    return response?.taxDetails !== undefined
      ? { ...(response?.taxDetails || {}), billingAddress: response?.billingAddress || null, requiresBillingAddress: !!response?.requiresBillingAddress }
      : response;
  }

  public async createSetUpIntent(requestBody: any) {
    return await this.invokeApi(
      HTTPMethod.POST,
      `${this.AUTH_API_ENDPOINT}/subscription?setupIntent=true`,
      requestBody
    );
  }

  public async createNcpCreditsPaymentIntent(requestBody: any) {
    return await this.invokeApi(
      HTTPMethod.POST,
      `${this.AUTH_API_ENDPOINT}/subscription?paymentIntent=true`,
      requestBody
    );
  }

  public async createFeatureCreditsPaymentIntent(requestBody: any) {
    return await this.invokeApi(
      HTTPMethod.POST,
      `${this.AUTH_API_ENDPOINT}/subscription?paymentIntent=true`,
      requestBody
    );
  }

  public async createResidentUnlockPaymentIntent(requestBody: any) {
    return await this.invokeApi(
      HTTPMethod.POST,
      `${this.AUTH_API_ENDPOINT}/subscription?paymentIntent=true`,
      requestBody
    );
  }

  public async createHomeUnlockPaymentIntent(requestBody: any) {
    return await this.invokeApi(
      HTTPMethod.POST,
      `${this.AUTH_API_ENDPOINT}/subscription?paymentIntent=true`,
      requestBody
    );
  }

  public async createWebsitePublishCreditPaymentIntent(requestBody: any) {
    return await this.invokeApi(
      HTTPMethod.POST,
      `${this.AUTH_API_ENDPOINT}/subscription?paymentIntent=true`,
      requestBody
    );
  }

  public async createWebsiteTemplateCreditPaymentIntent(requestBody: any) {
    return await this.invokeApi(
      HTTPMethod.POST,
      `${this.AUTH_API_ENDPOINT}/subscription?paymentIntent=true`,
      requestBody
    );
  }

  public async chargeSavedNcpCredits(requestBody: any) {
    return await this.invokeApi(
      HTTPMethod.POST,
      `${this.AUTH_API_ENDPOINT}/subscription?savedPaymentIntent=true`,
      requestBody
    );
  }

  public async chargeSavedFeatureCredits(requestBody: any) {
    return await this.invokeApi(
      HTTPMethod.POST,
      `${this.AUTH_API_ENDPOINT}/subscription?savedPaymentIntent=true`,
      requestBody
    );
  }

  public async createSubscriptionUpgradePaymentIntent(requestBody: any) {
    return await this.invokeApi(
      HTTPMethod.POST,
      `${this.AUTH_API_ENDPOINT}/subscription?paymentIntent=true`,
      requestBody
    );
  }

  public async createSubscriptionPlanUpdatePaymentIntent(requestBody: any) {
    return await this.invokeApi(
      HTTPMethod.POST,
      `${this.AUTH_API_ENDPOINT}/subscription?paymentIntent=true`,
      requestBody
    );
  }

  public async chargeSavedSubscriptionUpgrade(requestBody: any) {
    return await this.invokeApi(
      HTTPMethod.POST,
      `${this.AUTH_API_ENDPOINT}/subscription?savedPaymentIntent=true`,
      requestBody
    );
  }

  public async chargeSavedSubscriptionPlanUpdate(requestBody: any) {
    return await this.invokeApi(
      HTTPMethod.POST,
      `${this.AUTH_API_ENDPOINT}/subscription?savedPaymentIntent=true`,
      requestBody
    );
  }

  public async chargeSavedResidentUnlock(requestBody: any) {
    return await this.invokeApi(
      HTTPMethod.POST,
      `${this.AUTH_API_ENDPOINT}/subscription?savedPaymentIntent=true`,
      requestBody
    );
  }

  public async chargeSavedHomeUnlock(requestBody: any) {
    return await this.invokeApi(
      HTTPMethod.POST,
      `${this.AUTH_API_ENDPOINT}/subscription?savedPaymentIntent=true`,
      requestBody
    );
  }

  public async chargeSavedWebsitePublishCredit(requestBody: any) {
    return await this.invokeApi(
      HTTPMethod.POST,
      `${this.AUTH_API_ENDPOINT}/subscription?savedPaymentIntent=true`,
      requestBody
    );
  }

  public async chargeSavedWebsiteTemplateCredit(requestBody: any) {
    return await this.invokeApi(
      HTTPMethod.POST,
      `${this.AUTH_API_ENDPOINT}/subscription?savedPaymentIntent=true`,
      requestBody
    );
  }

  public async finalizeNcpCreditPayment(requestBody: any) {
    return await this.invokeApi(
      HTTPMethod.POST,
      `${this.AUTH_API_ENDPOINT}/subscription?finalizePaymentIntent=true`,
      requestBody
    );
  }

  public async finalizeFeatureCreditPayment(requestBody: any) {
    return await this.invokeApi(
      HTTPMethod.POST,
      `${this.AUTH_API_ENDPOINT}/subscription?finalizePaymentIntent=true`,
      requestBody
    );
  }

  public async finalizeSubscriptionUpgradePayment(requestBody: any) {
    return await this.invokeApi(
      HTTPMethod.POST,
      `${this.AUTH_API_ENDPOINT}/subscription?finalizePaymentIntent=true`,
      requestBody
    );
  }

  public async finalizeSubscriptionPlanUpdatePayment(requestBody: any) {
    return await this.invokeApi(
      HTTPMethod.POST,
      `${this.AUTH_API_ENDPOINT}/subscription?finalizePaymentIntent=true`,
      requestBody
    );
  }

  public async finalizeResidentUnlockPayment(requestBody: any) {
    return await this.invokeApi(
      HTTPMethod.POST,
      `${this.AUTH_API_ENDPOINT}/subscription?finalizePaymentIntent=true`,
      requestBody
    );
  }

  public async createCardUpdateSetupIntent(requestBody: any) {
    return await this.invokeApi(
      HTTPMethod.POST,
      `${this.AUTH_API_ENDPOINT}/subscription?cardSetupIntent=true`,
      requestBody
    );
  }

  public async finalizeCardUpdate(requestBody: any) {
    return await this.invokeApi(
      HTTPMethod.POST,
      `${this.AUTH_API_ENDPOINT}/subscription?updatePaymentMethod=true`,
      requestBody
    );
  }

  public async retryFailedPayment(requestBody: any) {
    return await this.invokeApi(
      HTTPMethod.POST,
      `${this.AUTH_API_ENDPOINT}/subscription?retryPayment=true`,
      requestBody
    );
  }

  public async listSubscription() {
    return await this.invokeApi(
      HTTPMethod.GET,
      `${this.AUTH_API_ENDPOINT}/subscription?listall=true`,
    );
  }

  public async createSubscription(requestBody: any) {
    return await this.invokeApi(
      HTTPMethod.POST,
      `${this.AUTH_API_ENDPOINT}/subscription`,
      requestBody,
    );
  }

  public async updateSubscription(requestBody: any) {
    return await this.invokeApi(
      HTTPMethod.PUT,
      `${this.AUTH_API_ENDPOINT}/subscription`,
      requestBody,
    );
  }

  public async cancelSubscription(requestBody: any) {
    return await this.invokeApi(
      HTTPMethod.PUT,
      `${this.AUTH_API_ENDPOINT}/subscription?cancel=true`,
      requestBody,
    );
  }

  public async cancelSubscriptionNow(requestBody: any) {
    return await this.invokeApi(
      HTTPMethod.PUT,
      `${this.AUTH_API_ENDPOINT}/subscription?cancelNow=true`,
      requestBody,
    );
  }

  public async resumeSubscription(requestBody: any) {
    return await this.invokeApi(
      HTTPMethod.PUT,
      `${this.AUTH_API_ENDPOINT}/subscription?resume=true`,
      requestBody,
    );
  }

  public async removeSubscriptionPlan(requestBody: any) {
    return await this.invokeApi(
      HTTPMethod.PUT,
      `${this.AUTH_API_ENDPOINT}/subscription?removePlan=true`,
      requestBody,
    );
  }

  public async getUserProfile(userId: string) {
    return await this.invokeApi(
      HTTPMethod.GET,
      `${this.AUTH_API_ENDPOINT}/userProfile?userId=${encodeURIComponent(userId)}`,
    );
  }

  public async updateUserProfile(userProfileData: any) {
    return await this.invokeApi(
      HTTPMethod.PUT,
      `${this.AUTH_API_ENDPOINT}/userProfile`,
      userProfileData,
    );
  }

  public async deleteUserProfile(userId: string) {
    return await this.invokeApi(
      HTTPMethod.DELETE,
      `${this.AUTH_API_ENDPOINT}/userProfile?userId=${encodeURIComponent(userId)}`,
    );
  }

  public async adminListUsers(options?: {
    filter?: string;
    limit?: number;
    paginationToken?: string;
    directoryView?: boolean;
    excludePrefixes?: string;
    roleType?: string;
  }) {
    const requestBody = {
      filter: options?.filter,
      limit: options?.limit || 100,
      paginationToken: options?.paginationToken,
      directoryView: options?.directoryView === true,
      excludePrefixes: options?.excludePrefixes || "",
      roleType: options?.roleType || "",
    };
    return await this.invokeApi(
      HTTPMethod.POST,
      `${this.AUTH_API_ENDPOINT}/user`,
      requestBody,
    );
  }

  public async adminDeleteUser(username: string) {
    return await this.invokeApi(
      HTTPMethod.DELETE,
      `${this.AUTH_API_ENDPOINT}/user?userName=${encodeURIComponent(username)}`,
    );
  }

  public async adminEnableDisableUser(username: string, enable: boolean) {
    return await this.invokeApi(
      HTTPMethod.PATCH,
      `${this.AUTH_API_ENDPOINT}/user?userName=${encodeURIComponent(username)}&enable=${enable}`,
    );
  }

  public async sendAdminCampaignEmail(requestBody: {
    templateType: string;
    subject: string;
    introText?: string;
    htmlBody: string;
    manualToEmails: string[];
    sendToAllActiveUsers: boolean;
    roleFilters?: string[];
  }) {
    return await this.invokeApi(
      HTTPMethod.POST,
      `${this.AUTH_API_ENDPOINT}/user?campaignEmail=true`,
      requestBody,
    );
  }

  public async previewAdminCampaignEmail(requestBody: {
    manualToEmails: string[];
    sendToAllActiveUsers: boolean;
    roleFilters?: string[];
  }) {
    return await this.invokeApi(
      HTTPMethod.POST,
      `${this.AUTH_API_ENDPOINT}/user?campaignEmailPreview=true`,
      requestBody,
    );
  }

  public async getEmailCampaignHistory(campaignId: string = "") {
    return await this.invokeApi(
      HTTPMethod.GET,
      `${this.AUTH_API_ENDPOINT}/user?campaignEmailHistory=true${campaignId ? `&campaignId=${encodeURIComponent(campaignId)}` : ""}`,
    );
  }

  public async retryUnattemptedCampaignEmails(campaignId: string) {
    return await this.invokeApi(
      HTTPMethod.POST,
      `${this.AUTH_API_ENDPOINT}/user?campaignEmailRetryUnattempted=true`,
      { campaignId },
    );
  }

  public async retryFailedCampaignEmails(campaignId: string) {
    return await this.invokeApi(
      HTTPMethod.POST,
      `${this.AUTH_API_ENDPOINT}/user?campaignEmailRetryFailed=true`,
      { campaignId },
    );
  }

  public async getCampaignEmailPreference(token: string) {
    return await this.invokeApi(
      HTTPMethod.GET,
      `${this.AUTH_API_ENDPOINT}/campaignPreferences?token=${encodeURIComponent(token)}`,
    );
  }

  public async updateCampaignEmailPreference(requestBody: {
    token: string;
    subscribe: boolean;
  }) {
    return await this.invokeApi(
      HTTPMethod.POST,
      `${this.AUTH_API_ENDPOINT}/campaignPreferences`,
      requestBody,
    );
  }

  public async getCurrentUserPreferences() {
    return await this.invokeApi(
      HTTPMethod.GET,
      `${this.AUTH_API_ENDPOINT}/userPreferences`,
    );
  }

  public async updateCurrentUserPreferences(requestBody: {
    emailCampaignSubscribed?: boolean;
    binderJobQuestionTemplates?: Array<{
      templateId: string;
      name: string;
      questions: string;
      updatedAt?: string;
    }>;
  }) {
    return await this.invokeApi(
      HTTPMethod.PUT,
      `${this.AUTH_API_ENDPOINT}/userPreferences`,
      requestBody,
    );
  }

  public async searchRegisteredNurses(params: {
    query: string;
    searchBy?: string;
    pageSize?: number;
    page?: number;
  }): Promise<{
    query: string;
    searchBy: string;
    page: number;
    pageSize: number;
    totalResults: number;
    hasMore: boolean;
    items: NurseDirectoryEntry[];
  }> {
    const searchParams = new URLSearchParams({
      search: params.query || "",
      searchBy: params.searchBy || "all",
      pageSize: String(params.pageSize || 12),
      page: String(params.page || 1),
    });

    return await this.invokeApi(
      HTTPMethod.GET,
      `${this.NURSE_API_ENDPOINT}/nurseDirectorySearch?${searchParams.toString()}`,
    );
  }

  public async getAdminObservabilitySummary(range: string = "1d") {
    return await this.invokeApi(
      HTTPMethod.GET,
      `${this.AUTH_API_ENDPOINT}/user?observability=true&range=${encodeURIComponent(range)}`,
    );
  }

  public async getAdminActivityLogs(range: string, search: string) {
    return await this.invokeApi(
      HTTPMethod.GET,
      `${this.AUTH_API_ENDPOINT}/user?activityLogs=true&range=${encodeURIComponent(range)}&search=${encodeURIComponent(search || "")}`,
    );
  }

  public async getAdminAuditEvents(
    range: string,
    search: string,
    eventType: string = "",
    status: string = "",
    pageSize: number = 50,
    nextToken: string = "",
  ) {
    return await this.invokeApi(
      HTTPMethod.GET,
      `${this.AUTH_API_ENDPOINT}/user?auditEvents=true&range=${encodeURIComponent(range)}&search=${encodeURIComponent(search || "")}&eventType=${encodeURIComponent(eventType || "")}&status=${encodeURIComponent(status || "")}&pageSize=${encodeURIComponent(String(pageSize))}&nextToken=${encodeURIComponent(nextToken || "")}`,
    );
  }

  public async postReminder(requestBody: any) {
    return await this.invokeApi(
      HTTPMethod.POST,
      `${this.REMINDER_API_ENDPOINT}/reminder`,
      requestBody,
    );
  }

  public async updateReminder(requestBody: any) {
    return await this.invokeApi(
      HTTPMethod.PUT,
      `${this.REMINDER_API_ENDPOINT}/reminder`,
      requestBody,
    );
  }

  public async getReminder(reminderId: string) {
    return await this.invokeApi(
      HTTPMethod.GET,
      `${this.REMINDER_API_ENDPOINT}/reminder?reminderId=${reminderId}`,
    );
  }

  public async deleteReminder(reminderId: string) {
    return await this.invokeApi(
      HTTPMethod.DELETE,
      `${this.REMINDER_API_ENDPOINT}/reminder?reminderId=${reminderId}`,
    );
  }

  public async listRemindersForCurrentUser() {
    return await this.invokeApi(
      HTTPMethod.GET,
      `${this.REMINDER_API_ENDPOINT}/reminder?list=true`,
    );
  }

  public async listUpcomingRemindersForCurrentUser(upcomingMonths: number) {
    return await this.invokeApi(
      HTTPMethod.GET,
      `${this.REMINDER_API_ENDPOINT}/reminder?listUpcoming=true&upcomingMonths=${upcomingMonths}`,
    );
  }

  public async listAllPastRemindersForCurrentUser() {
    return await this.invokeApi(
      HTTPMethod.GET,
      `${this.REMINDER_API_ENDPOINT}/reminder?listAllPast=true`,
    );
  }

  public async searchPsychotropicMedicationList(queryText: string, limit: number = 10, drugClass?: string) {
    const params = new URLSearchParams();
    if (queryText) {
      params.set("q", queryText);
    }
    if (limit) {
      params.set("limit", String(limit));
    }
    if (drugClass) {
      params.set("drugClass", drugClass);
    }

    return await this.invokeApi(
      HTTPMethod.GET,
      `${this.MEDICATION_API_ENDPOINT}/psychotropicMedicationList?${params.toString()}`,
    );
  }

  public async listPsychotropicMedicationClasses(queryText: string = "") {
    const params = new URLSearchParams();
    if (queryText) {
      params.set("q", queryText);
    }

    return await this.invokeApi(
      HTTPMethod.GET,
      `${this.MEDICATION_API_ENDPOINT}/psychotropicMedicationList/classes?${params.toString()}`,
    );
  }

  public async getResidentEmarMedications(residentId: string) {
    return await this.invokeApi(
      HTTPMethod.GET,
      `${this.MEDICATION_API_ENDPOINT}/residentEmarMedication?residentId=${encodeURIComponent(residentId)}`,
    );
  }

  public async createResidentEmarMedication(payload: any) {
    return await this.invokeApi(
      HTTPMethod.POST,
      `${this.MEDICATION_API_ENDPOINT}/residentEmarMedication`,
      payload,
    );
  }

  public async updateResidentEmarMedication(payload: any) {
    return await this.invokeApi(
      HTTPMethod.PUT,
      `${this.MEDICATION_API_ENDPOINT}/residentEmarMedication`,
      payload,
    );
  }

  public async resolveResidentEmarProtocol(payload: any) {
    return await this.invokeApi(
      HTTPMethod.POST,
      `${this.MEDICATION_API_ENDPOINT}/residentEmarProtocol/resolve`,
      payload,
    );
  }

  public async archiveResidentEmarMedication(
    residentId: string,
    medicationId: string,
    archiveDetails?: {
      archiveReason?: string;
      archiveReasonLabel?: string;
      archiveNote?: string;
    },
  ) {
    return await this.invokeApi(
      HTTPMethod.DELETE,
      `${this.MEDICATION_API_ENDPOINT}/residentEmarMedication?residentId=${encodeURIComponent(residentId)}&medicationId=${encodeURIComponent(medicationId)}`,
      archiveDetails,
    );
  }

  public async importResidentEmarMedicationsFromCarePlan(payload: {
    residentId: string;
    previewOnly?: boolean;
    selectedPreviewKeys?: string[];
  }) {
    return await this.invokeApi(
      HTTPMethod.POST,
      `${this.MEDICATION_API_ENDPOINT}/residentEmarMedication/import/carePlan`,
      payload,
    );
  }

  public async importResidentEmarMedicationsFromMarSheetFile(payload: {
    residentId: string;
    fileName: string;
    fileContentBase64?: string;
    s3ObjectKey?: string;
    previewOnly?: boolean;
    selectedPreviewKeys?: string[];
    preparedEntries?: any[];
  }) {
    return await this.invokeApi(
      HTTPMethod.POST,
      `${this.MEDICATION_API_ENDPOINT}/residentEmarMedication/import/marSheetFile`,
      payload,
    );
  }

  public async compareResidentCarePlanMedicationsFromMarSheetFile(payload: {
    residentId: string;
    fileName: string;
    fileContentBase64?: string;
    s3ObjectKey?: string;
    previewOnly?: boolean;
    useExistingCredit?: boolean;
  }) {
    return await this.invokeApi(
      HTTPMethod.POST,
      `${this.MEDICATION_API_ENDPOINT}/residentCarePlanMedicationCompare`,
      payload,
    );
  }

  public async compareResidentEmarMedicationsFromMarSheetFile(payload: {
    residentId: string;
    fileName: string;
    fileContentBase64?: string;
    s3ObjectKey?: string;
    previewOnly?: boolean;
    useExistingCredit?: boolean;
  }) {
    return await this.invokeApi(
      HTTPMethod.POST,
      `${this.MEDICATION_API_ENDPOINT}/residentEmarMedicationCompare`,
      payload,
    );
  }

  public async compareResidentEmarPsychotropicMedicationsWithCarePlan(payload: {
    residentId: string;
  }) {
    return await this.invokeApi(
      HTTPMethod.POST,
      `${this.MEDICATION_API_ENDPOINT}/residentEmarPsychotropicMedicationCompare`,
      payload,
    );
  }

  public async applyResidentEmarMedicationCompareJob(payload: {
    residentId: string;
    jobId: string;
  }) {
    return await this.invokeApi(
      HTTPMethod.PUT,
      `${this.MEDICATION_API_ENDPOINT}/residentEmarMedicationCompare`,
      payload,
    );
  }

  public async compareResidentCarePlanMedListWithNewMarSheet(payload: {
    residentId: string;
    baselineBinderDocumentId: string;
    fileName: string;
    fileContentBase64?: string;
    s3ObjectKey?: string;
    previewOnly?: boolean;
  }) {
    return await this.invokeApi(
      HTTPMethod.POST,
      `${this.MEDICATION_API_ENDPOINT}/residentCarePlanMedicationCompareFromBinder`,
      payload,
    );
  }

  public async applyResidentCarePlanMedicationCompareJob(payload: {
    residentId: string;
    jobId: string;
  }) {
    return await this.invokeApi(
      HTTPMethod.PUT,
      `${this.MEDICATION_API_ENDPOINT}/residentCarePlanMedicationCompareFromBinder`,
      payload,
    );
  }

  public async getResidentEmarEntries(
    residentId: string,
    options?: { entryDate?: string; startDate?: string; endDate?: string; limit?: number },
  ) {
    const params = new URLSearchParams({ residentId });
    if (options?.entryDate) {
      params.set("entryDate", options.entryDate);
    }
    if (options?.startDate) {
      params.set("startDate", options.startDate);
    }
    if (options?.endDate) {
      params.set("endDate", options.endDate);
    }
    if (options?.limit) {
      params.set("limit", String(options.limit));
    }
    return await this.invokeApi(
      HTTPMethod.GET,
      `${this.MEDICATION_API_ENDPOINT}/residentEmarEntry?${params.toString()}`,
    );
  }

  public async getResidentEmarPrnHistory(residentId: string, options?: { medicationId?: string; limit?: number; offset?: number }) {
    const params = new URLSearchParams({ residentId });
    if (options?.medicationId) params.set("medicationId", options.medicationId);
    if (options?.limit) params.set("limit", String(options.limit));
    if (options?.offset) params.set("offset", String(options.offset));
    return await this.invokeApi(HTTPMethod.GET, `${this.MEDICATION_API_ENDPOINT}/residentEmarPrnHistory?${params.toString()}`);
  }

  public async getResidentEmarDailyTrackingItems(residentId: string) {
    return await this.invokeApi(
      HTTPMethod.GET,
      `${this.MEDICATION_API_ENDPOINT}/residentEmarDailyTrackingItem?residentId=${encodeURIComponent(residentId)}`,
    );
  }

  public async createResidentEmarDailyTrackingItem(payload: any) {
    return await this.invokeApi(
      HTTPMethod.POST,
      `${this.MEDICATION_API_ENDPOINT}/residentEmarDailyTrackingItem`,
      payload,
    );
  }

  public async updateResidentEmarDailyTrackingItem(payload: any) {
    return await this.invokeApi(
      HTTPMethod.PUT,
      `${this.MEDICATION_API_ENDPOINT}/residentEmarDailyTrackingItem`,
      payload,
    );
  }

  public async deleteResidentEmarDailyTrackingItem(residentId: string, trackingItemId: string) {
    return await this.invokeApi(
      HTTPMethod.DELETE,
      `${this.MEDICATION_API_ENDPOINT}/residentEmarDailyTrackingItem?residentId=${encodeURIComponent(residentId)}&trackingItemId=${encodeURIComponent(trackingItemId)}`,
    );
  }

  public async getResidentEmarDailyTrackingEntries(
    residentId: string,
    options?: { entryDate?: string; startDate?: string; endDate?: string; limit?: number },
  ) {
    const params = new URLSearchParams({ residentId });
    if (options?.entryDate) {
      params.set("entryDate", options.entryDate);
    }
    if (options?.startDate) {
      params.set("startDate", options.startDate);
    }
    if (options?.endDate) {
      params.set("endDate", options.endDate);
    }
    if (options?.limit) {
      params.set("limit", String(options.limit));
    }
    return await this.invokeApi(
      HTTPMethod.GET,
      `${this.MEDICATION_API_ENDPOINT}/residentEmarDailyTrackingEntry?${params.toString()}`,
    );
  }

  public async upsertResidentEmarDailyTrackingEntry(payload: any) {
    return await this.invokeApi(
      HTTPMethod.POST,
      `${this.MEDICATION_API_ENDPOINT}/residentEmarDailyTrackingEntry`,
      payload,
    );
  }

  public async getResidentEmarBoardSummary(residentIds: string[], entryDate?: string) {
    const normalizedResidentIds = (residentIds || [])
      .map((residentId) => String(residentId || "").trim())
      .filter(Boolean);
    if (!normalizedResidentIds.length) {
      return { items: [] };
    }
    const params = new URLSearchParams({
      residentIds: normalizedResidentIds.join(","),
    });
    if (entryDate) {
      params.set("entryDate", entryDate);
    }
    return await this.invokeApi(
      HTTPMethod.GET,
      `${this.MEDICATION_API_ENDPOINT}/residentEmarBoardSummary?${params.toString()}`,
    );
  }

  public async upsertResidentEmarEntry(payload: any) {
    return await this.invokeApi(
      HTTPMethod.POST,
      `${this.MEDICATION_API_ENDPOINT}/residentEmarEntry`,
      payload,
    );
  }

  public async updateResidentEmarEntryException(payload: any) {
    return await this.invokeApi(
      HTTPMethod.PUT,
      `${this.MEDICATION_API_ENDPOINT}/residentEmarEntry`,
      payload,
    );
  }
}
